import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import { isSocio } from '../utils/permissions';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  ClipboardCheck, Calendar, Users, AlertTriangle, CheckCircle, 
  MessageSquare, Send, ChevronDown, ChevronRight, RotateCcw, Clock, FileText, Plus, Search, Briefcase, Trash2,
  Star, ThumbsUp, AlertCircle, UserCheck, ArrowRight, Paperclip, Key, Download, RefreshCw, Play, Pause, History,
  Flame, Zap
} from 'lucide-react';
import api, { formatErrorMessage } from '../utils/api';
import { toast } from 'sonner';
import TaskAttachments from '../components/TaskAttachments';
import ClientCredentials from '../components/ClientCredentials';
import MentionInput from '../components/MentionInput';

// Helper para obtener el estilo de prioridad
const getPriorityStyle = (priority) => {
  switch (priority) {
    case 1:
      return {
        label: 'Urgente',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        borderColor: 'border-red-400',
        icon: Flame,
        iconColor: 'text-red-500'
      };
    case 3:
      return {
        label: 'Baja',
        bgColor: 'bg-slate-100',
        textColor: 'text-slate-600',
        borderColor: 'border-slate-300',
        icon: null,
        iconColor: ''
      };
    default: // 2 = Regular
      return {
        label: 'Regular',
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-700',
        borderColor: 'border-blue-200',
        icon: null,
        iconColor: ''
      };
  }
};

const MisTareas = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [tareas, setTareas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedTareas, setExpandedTareas] = useState({});
  const [showGestion, setShowGestion] = useState(false);
  const [showCumplida, setShowCumplida] = useState(false);
  const [showCrearTarea, setShowCrearTarea] = useState(false);
  const [selectedTarea, setSelectedTarea] = useState(null);
  const [gestionText, setGestionText] = useState('');
  const [comentarioCumplida, setComentarioCumplida] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [nuevaTarea, setNuevaTarea] = useState({
    title: '',
    description: '',
    area: '',
    deadline: '',
    priority: 2,  // Default: Regular
    proposal_id: ''
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [pspsActivas, setPspsActivas] = useState([]);
  const [pspSearch, setPspSearch] = useState('');
  const [loadingPsps, setLoadingPsps] = useState(false);
  const [selectedPsp, setSelectedPsp] = useState(null);
  const [showEliminar, setShowEliminar] = useState(false);
  const [tareaAEliminar, setTareaAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);
  
  // Estados para revisión de socios
  const [showRevisarSocio, setShowRevisarSocio] = useState(false);
  const [feedbackSocio, setFeedbackSocio] = useState('');
  const [submittingSocio, setSubmittingSocio] = useState(false);
  const [adjuntosSocio, setAdjuntosSocio] = useState([]);

  // Estado para ver documentos de una tarea
  const [showVerDocumentos, setShowVerDocumentos] = useState(false);

  // Estado para ver credenciales de una tarea
  const [showCredencialesTarea, setShowCredencialesTarea] = useState(false);

  // Estados para gestión de líder (tareas devueltas por socios)
  const [showGestionLider, setShowGestionLider] = useState(false);
  const [comentarioLider, setComentarioLider] = useState('');
  const [accionLider, setAccionLider] = useState(''); // 'auto_resolver' o 'derivar'
  const [submittingLider, setSubmittingLider] = useState(false);
  const [funcionarioDerivacion, setFuncionarioDerivacion] = useState({ name: '', id: '' });
  const [plazoDerivacion, setPlazoDerivacion] = useState('');
  const [usuariosEquipo, setUsuariosEquipo] = useState([]);
  const [adjuntosGestionLider, setAdjuntosGestionLider] = useState([]);

  // Estado para menciones en bitácora
  const [mencionesGestion, setMencionesGestion] = useState([]);
  const [mencionesCumplida, setMencionesCumplida] = useState([]);
  const [mencionesSocio, setMencionesSocio] = useState([]);
  const [mencionesLider, setMencionesLider] = useState([]);

  // Estado para referencias por responder
  const [referenciasPendientes, setReferenciasPendientes] = useState([]);
  const [showResponderReferencia, setShowResponderReferencia] = useState(false);
  const [referenciaSeleccionada, setReferenciaSeleccionada] = useState(null);
  const [respuestaReferencia, setRespuestaReferencia] = useState('');
  const [adjuntosRespuesta, setAdjuntosRespuesta] = useState([]);
  const [respondiendoRef, setRespondiendoRef] = useState(false);

  // Estados para modales de referencias (Ver bitácora, credenciales, adjuntos, minutas)
  const [showBitacoraRef, setShowBitacoraRef] = useState(false);
  const [showCredencialesRef, setShowCredencialesRef] = useState(false);
  const [showAdjuntosRef, setShowAdjuntosRef] = useState(false);
  const [showMinutasRef, setShowMinutasRef] = useState(false);
  const [tareaRefSeleccionada, setTareaRefSeleccionada] = useState(null);
  const [minutasPsp, setMinutasPsp] = useState([]);
  const [loadingMinutas, setLoadingMinutas] = useState(false);

  // Estado para tareas asignadas por otros líderes/socios
  const [tareasAsignadasPorLideres, setTareasAsignadasPorLideres] = useState([]);

  // Estados para Tareas Periódicas
  const [activeTab, setActiveTab] = useState('pendientes'); // 'pendientes' o 'periodicas'
  const [tareasPeriodicasList, setTareasPeriodicasList] = useState([]);
  const [loadingPeriodicas, setLoadingPeriodicas] = useState(false);
  const [showCrearPeriodica, setShowCrearPeriodica] = useState(false);
  const [showCumplirPeriodica, setShowCumplirPeriodica] = useState(false);
  const [showHistorialPeriodica, setShowHistorialPeriodica] = useState(false);
  const [periodicaSeleccionada, setPeriodicaSeleccionada] = useState(null);
  const [periodicaForm, setPeriodicaForm] = useState({
    titulo: '',
    descripcion: '',
    frecuencia: 'mensual',
    repeticiones_total: 12,
    fecha_inicio: '',
    dia_semana: null,
    dia_mes: 1,
    client_id: '',
    psp_id: '',
    sin_cliente: false
  });
  const [feedbackPeriodica, setFeedbackPeriodica] = useState('');
  const [adjuntosPeriodica, setAdjuntosPeriodica] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [psps, setPsps] = useState([]);
  const [pspsCliente, setPspsCliente] = useState([]); // PSPs filtradas por cliente
  const [periodicasVencidas, setPeriodicasVencidas] = useState([]);

  // Roles que pueden eliminar tareas
  const rolesConPermisoEliminar = ['Administrador', 'Líder', 'Coordinador'];
  
  // Verificar si el usuario es socio (por rol/área — sin emails hardcodeados)
  const esSocio = isSocio(currentUser);

  // Verificar si el usuario es líder
  const esLider = currentUser && ['Administrador', 'Líder', 'Coordinador'].includes(currentUser.role);

  useEffect(() => {
    fetchTareas();
    fetchCurrentUser();
    fetchReferenciasPendientes();
    fetchPeriodicasVencidas();
    fetchTareasAsignadasPorLideres();
  }, []);

  // Cargar tareas periódicas cuando se cambia a la pestaña
  useEffect(() => {
    if (activeTab === 'periodicas') {
      fetchTareasPeriodicasList();
      fetchClientesYPsps();
    }
  }, [activeTab]);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
      // Preseleccionar el área del usuario
      if (response.data.areas?.length > 0) {
        setNuevaTarea(prev => ({ ...prev, area: response.data.areas[0] }));
      }
    } catch (error) {
      console.error('Error al cargar usuario');
    }
  };

  const fetchReferenciasPendientes = async () => {
    try {
      const response = await api.get('/referencias/pendientes');
      setReferenciasPendientes(response.data || []);
    } catch (error) {
      console.error('Error al cargar referencias:', error);
    }
  };

  const fetchPeriodicasVencidas = async () => {
    try {
      const response = await api.get('/tareas-periodicas/vencidas/pendientes');
      setPeriodicasVencidas(response.data || []);
    } catch (error) {
      console.error('Error al cargar periódicas vencidas:', error);
    }
  };

  const fetchTareasAsignadasPorLideres = async () => {
    try {
      const response = await api.get('/tasks/asignadas-por-lideres');
      setTareasAsignadasPorLideres(response.data || []);
    } catch (error) {
      console.error('Error al cargar tareas asignadas por líderes:', error);
    }
  };

  // ==================== FUNCIONES TAREAS PERIÓDICAS ====================
  
  const fetchTareasPeriodicasList = async () => {
    setLoadingPeriodicas(true);
    try {
      const response = await api.get('/tareas-periodicas?solo_propias=true');
      setTareasPeriodicasList(response.data || []);
    } catch (error) {
      toast.error('Error al cargar tareas periódicas');
    } finally {
      setLoadingPeriodicas(false);
    }
  };

  const fetchClientesYPsps = async () => {
    try {
      const [clientesRes, pspsRes] = await Promise.all([
        api.get('/clients'),
        api.get('/proposals?estado=Activa')
      ]);
      setClientes(clientesRes.data || []);
      setPsps(pspsRes.data || []);
    } catch (error) {
      console.error('Error al cargar clientes/PSPs');
    }
  };

  const handleCrearPeriodica = async () => {
    if (!periodicaForm.titulo.trim()) {
      toast.error('El título es obligatorio');
      return;
    }
    if (!periodicaForm.fecha_inicio) {
      toast.error('La fecha de inicio es obligatoria');
      return;
    }
    if (!periodicaForm.sin_cliente && !periodicaForm.client_id) {
      toast.error('Debe seleccionar un cliente o marcar "Sin cliente asociado"');
      return;
    }

    try {
      const payload = {
        ...periodicaForm,
        client_id: periodicaForm.sin_cliente ? null : periodicaForm.client_id,
        psp_id: periodicaForm.sin_cliente ? null : periodicaForm.psp_id,
        repeticiones_total: parseInt(periodicaForm.repeticiones_total) || 1,
        attachment_ids: adjuntosPeriodica.map(a => a.id).filter(Boolean)
      };
      delete payload.sin_cliente; // No enviar este campo al backend
      
      await api.post('/tareas-periodicas', payload);
      toast.success('Tarea periódica creada exitosamente');
      setShowCrearPeriodica(false);
      setPeriodicaForm({
        titulo: '',
        descripcion: '',
        frecuencia: 'mensual',
        repeticiones_total: 12,
        fecha_inicio: '',
        dia_semana: null,
        dia_mes: 1,
        client_id: '',
        psp_id: '',
        sin_cliente: false
      });
      setAdjuntosPeriodica([]);
      setPspsCliente([]);
      fetchTareasPeriodicasList();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  // Filtrar PSPs por cliente seleccionado
  const handleClienteChange = async (clientId) => {
    setPeriodicaForm(prev => ({ ...prev, client_id: clientId, psp_id: '' }));
    if (clientId) {
      try {
        // Obtener todas las PSPs y filtrar por client_id y estados activos
        const response = await api.get(`/proposals?client_id=${clientId}`);
        const pspsActivas = (response.data || []).filter(p => 
          ['Aceptada', 'Ejecución/Activa'].includes(p.status)
        );
        setPspsCliente(pspsActivas);
      } catch (error) {
        setPspsCliente([]);
      }
    } else {
      setPspsCliente([]);
    }
  };

  const handleCumplirPeriodica = async () => {
    if (!feedbackPeriodica.trim() || feedbackPeriodica.trim().length < 5) {
      toast.error('El feedback es obligatorio (mínimo 5 caracteres)');
      return;
    }

    try {
      await api.put(`/tareas-periodicas/${periodicaSeleccionada.id}/completar`, {
        feedback: feedbackPeriodica,
        attachment_ids: adjuntosPeriodica.map(a => a.id).filter(Boolean)
      });
      toast.success('Ciclo completado exitosamente');
      setShowCumplirPeriodica(false);
      setFeedbackPeriodica('');
      setAdjuntosPeriodica([]);
      setPeriodicaSeleccionada(null);
      fetchTareasPeriodicasList();
      fetchPeriodicasVencidas(); // Actualizar vencidas
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleEliminarPeriodica = async (tarea) => {
    if (!window.confirm(`¿Está seguro de eliminar la tarea periódica "${tarea.titulo}"?`)) {
      return;
    }
    try {
      await api.delete(`/tareas-periodicas/${tarea.id}`);
      toast.success('Tarea periódica eliminada');
      fetchTareasPeriodicasList();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handlePausarPeriodica = async (tarea) => {
    try {
      const nuevoEstado = tarea.estado === 'activa' ? 'pausada' : 'activa';
      await api.put(`/tareas-periodicas/${tarea.id}`, { estado: nuevoEstado });
      toast.success(nuevoEstado === 'pausada' ? 'Tarea pausada' : 'Tarea reactivada');
      fetchTareasPeriodicasList();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const getFrecuenciaLabel = (frecuencia) => {
    const labels = { diaria: 'Diaria', semanal: 'Semanal', mensual: 'Mensual' };
    return labels[frecuencia] || frecuencia;
  };

  const getEstadoColor = (estado) => {
    const colors = {
      activa: 'bg-green-100 text-green-700',
      pausada: 'bg-yellow-100 text-yellow-700',
      completada: 'bg-blue-100 text-blue-700'
    };
    return colors[estado] || 'bg-slate-100 text-slate-700';
  };

  // Función para descargar archivos adjuntos de gestiones
  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      const response = await api.get(`/files/${attachmentId}`, {
        responseType: 'blob'
      });
      
      const contentType = response.headers?.['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'archivo';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      // Fallback: abrir URL directamente
      try {
        window.open(`/api/files/${attachmentId}`, '_blank');
      } catch (fallbackError) {
        toast.error('Error al descargar archivo');
      }
    }
  };

  // Función para obtener info de archivos desde IDs
  const [attachmentsCache, setAttachmentsCache] = useState({});
  
  const fetchAttachmentInfo = async (attachmentId) => {
    if (attachmentsCache[attachmentId]) {
      return attachmentsCache[attachmentId];
    }
    try {
      const response = await api.get(`/files/${attachmentId}`);
      setAttachmentsCache(prev => ({ ...prev, [attachmentId]: response.data }));
      return response.data;
    } catch (error) {
      return null;
    }
  };

  const fetchPspsActivas = async (search = '') => {
    setLoadingPsps(true);
    try {
      const response = await api.get(`/proposals/activas-busqueda?search=${encodeURIComponent(search)}`);
      setPspsActivas(response.data);
    } catch (error) {
      console.error('Error al cargar PSPs');
      setPspsActivas([]);
    } finally {
      setLoadingPsps(false);
    }
  };

  // Cargar PSPs cuando se abre el modal de crear tarea
  useEffect(() => {
    if (showCrearTarea) {
      fetchPspsActivas(pspSearch);
    }
  }, [showCrearTarea, pspSearch]);

  const fetchTareas = async () => {
    setLoading(true);
    try {
      const response = await api.get('/tasks/mis-tareas');
      setTareas(response.data);
      
      // Expandir todos los grupos por defecto
      const grupos = agruparTareas(response.data);
      const expanded = {};
      grupos.forEach((g, idx) => { expanded[idx] = true; });
      setExpandedGroups(expanded);
    } catch (error) {
      toast.error('Error al cargar tareas');
    } finally {
      setLoading(false);
    }
  };

  const toggleGroupExpanded = (idx) => {
    setExpandedGroups(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const toggleTareaExpanded = (taskId) => {
    setExpandedTareas(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  // Verificar si el usuario puede eliminar tareas
  const puedeEliminar = currentUser && rolesConPermisoEliminar.includes(currentUser.role);

  const handleEliminarTarea = async () => {
    if (!tareaAEliminar) return;
    
    setEliminando(true);
    try {
      await api.delete(`/tasks/${tareaAEliminar.id}`);
      toast.success('Tarea eliminada correctamente');
      setShowEliminar(false);
      setTareaAEliminar(null);
      fetchTareas();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setEliminando(false);
    }
  };

  // Función para notificar menciones
  const notificarMenciones = async (menciones, contexto, mensaje, urlContexto, taskId = null) => {
    if (!menciones || menciones.length === 0) return;
    
    try {
      await api.post('/menciones/notificar', {
        mencionados: menciones.map(m => m.id),
        contexto,
        mensaje,
        url_contexto: urlContexto,
        task_id: taskId
      });
    } catch (error) {
      console.error('Error al notificar menciones:', error);
    }
  };

  // Función para responder a una referencia
  const handleResponderReferencia = async () => {
    if (!referenciaSeleccionada || !respuestaReferencia.trim()) {
      toast.error('Escriba su respuesta');
      return;
    }

    setRespondiendoRef(true);
    try {
      const attachmentIds = (adjuntosRespuesta || []).filter(a => a?.id).map(a => a.id);
      
      await api.put(`/referencias/${referenciaSeleccionada.id}/responder`, {
        comentario: respuestaReferencia,
        attachment_ids: attachmentIds
      });

      toast.success('Referencia respondida correctamente');
      setShowResponderReferencia(false);
      setReferenciaSeleccionada(null);
      setRespuestaReferencia('');
      setAdjuntosRespuesta([]);
      fetchReferenciasPendientes();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setRespondiendoRef(false);
    }
  };

  // Abrir modal para responder referencia
  const handleOpenResponderReferencia = (referencia) => {
    setReferenciaSeleccionada(referencia);
    setRespuestaReferencia('');
    setAdjuntosRespuesta([]);
    setShowResponderReferencia(true);
  };

  // Funciones para ver información de la tarea asociada a una referencia
  const handleVerMinutaRef = async (referencia) => {
    if (!referencia.task_info?.psp_id) {
      toast.error('No hay PSP asociada a esta tarea');
      return;
    }
    
    setLoadingMinutas(true);
    setTareaRefSeleccionada(referencia.task_info);
    
    try {
      // Obtener las minutas de la PSP
      const response = await api.get(`/proposals/${referencia.task_info.psp_id}/minutas`);
      setMinutasPsp(response.data || []);
      setShowMinutasRef(true);
    } catch (error) {
      toast.error('Error al cargar las minutas de la PSP');
    } finally {
      setLoadingMinutas(false);
    }
  };

  const handleVerBitacoraRef = async (referencia) => {
    if (!referencia.task_info?.task_id) {
      toast.error('No hay tarea asociada');
      return;
    }
    try {
      const response = await api.get(`/tasks/detalle/${referencia.task_info.task_id}`);
      setTareaRefSeleccionada(response.data);
      setShowBitacoraRef(true);
    } catch (error) {
      toast.error('Error al cargar la bitácora');
    }
  };

  const handleVerCredencialesRef = async (referencia) => {
    if (!referencia.task_info?.client_id) {
      toast.error('No hay cliente asociado');
      return;
    }
    setTareaRefSeleccionada({ 
      client_id: referencia.task_info.client_id, 
      client_name: referencia.task_info.client_name,
      ...referencia.task_info 
    });
    setShowCredencialesRef(true);
  };

  const handleVerAdjuntosRef = async (referencia) => {
    if (!referencia.task_info?.task_id) {
      toast.error('No hay tarea asociada');
      return;
    }
    try {
      const response = await api.get(`/tasks/detalle/${referencia.task_info.task_id}`);
      setTareaRefSeleccionada(response.data);
      setShowAdjuntosRef(true);
    } catch (error) {
      toast.error('Error al cargar los adjuntos');
    }
  };

  const handleRegistrarGestion = async () => {
    if (!selectedTarea || !gestionText.trim()) {
      toast.error('Ingrese una descripción de la gestión');
      return;
    }

    setSubmitting(true);
    try {
      // Obtener IDs de los adjuntos actuales de la tarea (recién subidos)
      const attachmentIds = (selectedTarea.attachments || [])
        .filter(a => a && a.id)
        .map(a => a.id);
      
      let url = `/tasks/${selectedTarea.id}/registrar-gestion?descripcion=${encodeURIComponent(gestionText)}`;
      if (attachmentIds.length > 0) {
        url += `&attachments=${encodeURIComponent(JSON.stringify(attachmentIds))}`;
      }
      
      await api.post(url);
      
      // Notificar menciones si las hay (incluir task_id)
      if (mencionesGestion.length > 0) {
        await notificarMenciones(
          mencionesGestion,
          `tarea "${selectedTarea.title}"`,
          gestionText,
          `/mis-tareas?task=${selectedTarea.id}`,
          selectedTarea.id
        );
      }
      
      toast.success('Gestión registrada en la bitácora');
      setShowGestion(false);
      setGestionText('');
      setMencionesGestion([]);
      setSelectedTarea(null);
      fetchTareas();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCrearTarea = async () => {
    // Validar que se haya seleccionado una PSP
    if (!nuevaTarea.proposal_id) {
      toast.error('Debe seleccionar un Cliente/PSP. Toda tarea debe estar vinculada a un contrato.');
      return;
    }
    
    if (!nuevaTarea.title.trim() || !nuevaTarea.description.trim() || !nuevaTarea.area || !nuevaTarea.deadline) {
      toast.error('Complete todos los campos requeridos');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/tasks/crear-personal', {
        title: nuevaTarea.title,
        description: nuevaTarea.description,
        area: nuevaTarea.area,
        deadline: new Date(nuevaTarea.deadline).toISOString(),
        priority: nuevaTarea.priority || 2,  // Enviar prioridad (default 2)
        proposal_id: nuevaTarea.proposal_id
      });
      toast.success('Tarea creada exitosamente');
      setShowCrearTarea(false);
      setNuevaTarea({
        title: '',
        description: '',
        area: currentUser?.areas?.[0] || '',
        deadline: '',
        priority: 2,  // Reset a default
        proposal_id: ''
      });
      setSelectedPsp(null);
      setPspSearch('');
      fetchTareas();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectPsp = (psp) => {
    setSelectedPsp(psp);
    setNuevaTarea({ ...nuevaTarea, proposal_id: psp.id });
    setPspSearch('');
  };

  const handleDarPorCumplida = async () => {
    if (!selectedTarea) return;
    
    // Comentario de cierre es OBLIGATORIO
    if (!comentarioCumplida.trim()) {
      toast.error('El comentario de cierre es obligatorio para la auditoría');
      return;
    }

    setSubmitting(true);
    try {
      // Obtener IDs de los adjuntos actuales de la tarea (recién subidos)
      const attachmentIds = (selectedTarea.attachments || [])
        .filter(a => a && a.id)
        .map(a => a.id);
      
      let url = `/tasks/${selectedTarea.id}/dar-por-cumplida?comentario=${encodeURIComponent(comentarioCumplida)}`;
      if (attachmentIds.length > 0) {
        url += `&attachments=${encodeURIComponent(JSON.stringify(attachmentIds))}`;
      }
      
      await api.put(url);
      toast.success('Tarea enviada a revisión del líder');
      setShowCumplida(false);
      setComentarioCumplida('');
      setSelectedTarea(null);
      fetchTareas();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  // Funciones de revisión de socios
  const handleAprobarSocio = async () => {
    if (!selectedTarea || !feedbackSocio.trim()) {
      toast.error('El feedback es obligatorio');
      return;
    }

    setSubmittingSocio(true);
    try {
      // Obtener IDs de los adjuntos del socio (recién subidos)
      const attachmentIds = (adjuntosSocio || [])
        .filter(a => a && a.id)
        .map(a => a.id);
      
      let url = `/tasks/${selectedTarea.id}/aprobar-socio?feedback=${encodeURIComponent(feedbackSocio)}`;
      if (attachmentIds.length > 0) {
        url += `&attachments=${encodeURIComponent(JSON.stringify(attachmentIds))}`;
      }
      
      await api.put(url);
      toast.success('Tarea aprobada exitosamente');
      setShowRevisarSocio(false);
      setFeedbackSocio('');
      setAdjuntosSocio([]);
      setSelectedTarea(null);
      fetchTareas();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSubmittingSocio(false);
    }
  };

  const handleDevolverSocio = async () => {
    if (!selectedTarea || !feedbackSocio.trim()) {
      toast.error('El feedback es obligatorio');
      return;
    }

    setSubmittingSocio(true);
    try {
      // Obtener IDs de los adjuntos del socio (recién subidos)
      const attachmentIds = (adjuntosSocio || [])
        .filter(a => a && a.id)
        .map(a => a.id);
      
      let url = `/tasks/${selectedTarea.id}/devolver-socio?feedback=${encodeURIComponent(feedbackSocio)}`;
      if (attachmentIds.length > 0) {
        url += `&attachments=${encodeURIComponent(JSON.stringify(attachmentIds))}`;
      }
      
      await api.put(url);
      toast.success('Tarea devuelta al líder que la escaló');
      setShowRevisarSocio(false);
      setFeedbackSocio('');
      setAdjuntosSocio([]);
      setSelectedTarea(null);
      fetchTareas();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSubmittingSocio(false);
    }
  };

  // Funciones para gestión de líder (tareas devueltas por socios)
  const handleOpenGestionLider = async (tarea) => {
    setSelectedTarea(tarea);
    setComentarioLider('');
    setAccionLider('');
    setFuncionarioDerivacion({ name: '', id: '' });
    setPlazoDerivacion('');
    setShowGestionLider(true);
    
    // Cargar usuarios del área para derivación
    try {
      const response = await api.get(`/users/asignables?area=${tarea.area || ''}`);
      setUsuariosEquipo([
        ...response.data.usuarios_area,
        ...response.data.otros_lideres
      ]);
    } catch (error) {
      console.error('Error cargando usuarios');
    }
  };

  const handleAutoResolverLider = async () => {
    if (!selectedTarea || !comentarioLider.trim()) {
      toast.error('El comentario es obligatorio');
      return;
    }

    setSubmittingLider(true);
    try {
      // Incluir adjuntos si existen
      const attachmentIds = (adjuntosGestionLider || [])
        .filter(a => a && a.id)
        .map(a => a.id);
      
      let url = `/tasks/${selectedTarea.id}/lider-auto-resolver?comentario=${encodeURIComponent(comentarioLider)}`;
      if (attachmentIds.length > 0) {
        url += `&attachments=${encodeURIComponent(JSON.stringify(attachmentIds))}`;
      }
      
      await api.put(url);
      toast.success('Tarea resuelta y reenviada a revisión de Socios');
      setShowGestionLider(false);
      setSelectedTarea(null);
      setComentarioLider('');
      setAdjuntosGestionLider([]);
      fetchTareas();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSubmittingLider(false);
    }
  };

  const handleDerivarFuncionario = async () => {
    if (!selectedTarea || !comentarioLider.trim() || !funcionarioDerivacion.id || !plazoDerivacion) {
      toast.error('Complete todos los campos requeridos');
      return;
    }

    setSubmittingLider(true);
    try {
      // Incluir adjuntos si existen
      const attachmentIds = (adjuntosGestionLider || [])
        .filter(a => a && a.id)
        .map(a => a.id);
      
      const params = new URLSearchParams({
        responsable: funcionarioDerivacion.name,
        responsable_id: funcionarioDerivacion.id,
        nuevo_plazo: new Date(plazoDerivacion).toISOString(),
        comentario: comentarioLider
      });
      
      if (attachmentIds.length > 0) {
        params.append('attachments', JSON.stringify(attachmentIds));
      }
      
      await api.put(`/tasks/${selectedTarea.id}/reasignar-devuelta?${params.toString()}`);
      toast.success(`Tarea derivada a ${funcionarioDerivacion.name} para corrección`);
      setShowGestionLider(false);
      setSelectedTarea(null);
      setComentarioLider('');
      setAdjuntosGestionLider([]);
      fetchTareas();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSubmittingLider(false);
    }
  };

  const handleViewPDF = async (meetingId) => {
    if (!meetingId) {
      toast.error('Esta tarea no tiene minuta asociada');
      return;
    }
    try {
      const response = await api.post(`/meetings/${meetingId}/generate-pdf`, null, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Error al generar PDF');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Devuelta':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'Devuelta por Socios':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'Revisión Socios':
        return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'En Progreso':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Pendiente':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const isOverdue = (deadline) => {
    return new Date(deadline) < new Date();
  };

  // Contar todos los adjuntos de una tarea (directos + de gestiones/bitácora)
  const getTotalAdjuntos = (tarea) => {
    let total = 0;
    // Adjuntos directos
    if (tarea.attachments?.length) {
      total += tarea.attachments.length;
    }
    // Adjuntos de gestiones (bitácora)
    if (tarea.gestiones?.length) {
      tarea.gestiones.forEach(g => {
        if (g.attachments?.length) {
          total += g.attachments.length;
        }
      });
    }
    return total;
  };

  // Agrupar tareas por Cliente - Minuta/PSP
  const agruparTareas = (tareasLista) => {
    const grupos = {};
    tareasLista.forEach(tarea => {
      const key = `${tarea.client_id || 'sin_cliente'}_${tarea.meeting_id || 'sin_minuta'}_${tarea.proposal_id || 'sin_psp'}`;
      if (!grupos[key]) {
        grupos[key] = {
          cliente: tarea.client_name || 'Sin Cliente',
          meeting_id: tarea.meeting_id,
          proposal_id: tarea.proposal_id,
          psp_folio: tarea.psp_folio,
          tareas: []
        };
      }
      grupos[key].tareas.push(tarea);
    });
    return Object.values(grupos);
  };

  // Componente para mostrar adjuntos de una gestión
  const GestionAttachments = ({ attachmentIds }) => {
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const loadAttachments = async () => {
        if (!attachmentIds?.length) {
          setLoading(false);
          return;
        }
        try {
          // Obtener info de todos los archivos usando el endpoint /info
          const promises = attachmentIds.map(id => 
            api.get(`/files/${id}/info`).then(r => r.data).catch(() => null)
          );
          const results = await Promise.all(promises);
          setAttachments(results.filter(Boolean));
        } catch (error) {
          console.error('Error al cargar adjuntos:', error);
        } finally {
          setLoading(false);
        }
      };
      loadAttachments();
    }, [attachmentIds]);

    if (loading || attachments.length === 0) return null;

    return (
      <div className="mt-2 pt-2 border-t border-slate-200/50">
        <div className="flex flex-wrap gap-2">
          {attachments.map((file) => (
            <button
              key={file.id}
              onClick={() => handleDownloadAttachment(file.id, file.original_name)}
              className="inline-flex items-center gap-1.5 px-2 py-1 bg-white border border-slate-200 rounded-md text-xs text-slate-600 hover:bg-slate-50 hover:border-blue-300 hover:text-blue-600 transition-colors"
              title={`Descargar ${file.original_name}`}
            >
              <Download className="w-3.5 h-3.5" />
              <span className="max-w-[120px] truncate">{file.original_name}</span>
              <span className="text-slate-400">({(file.size / 1024).toFixed(0)} KB)</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Separar tareas por tipo
  const tareasRevisionSocios = tareas.filter(t => t.status === 'Revisión Socios');
  const tareasDevueltasPorSocios = tareas.filter(t => t.status === 'Devuelta por Socios' && t.requiere_accion_lider);
  const tareasDevueltas = tareas.filter(t => t.status === 'Devuelta');
  // Filtrar también las tareas asignadas por líderes (se muestran en sección especial)
  const tareasPendientes = tareas.filter(t => 
    !['Devuelta', 'Devuelta por Socios', 'Revisión Socios'].includes(t.status) &&
    !t.asignado_por_lider
  );
  
  const gruposRevisionSocios = agruparTareas(tareasRevisionSocios);
  const gruposDevueltasPorSocios = agruparTareas(tareasDevueltasPorSocios);
  const gruposDevueltas = agruparTareas(tareasDevueltas);
  const gruposPendientes = agruparTareas(tareasPendientes);

  const renderTareaCard = (tarea, tipo = 'normal') => (
    <Card 
      key={tarea.id} 
      className={`ml-6 border-l-4 ${
        tarea.priority === 1
          ? 'border-l-red-500 bg-red-50/20 ring-1 ring-red-200'
          : tipo === 'revision_socios'
            ? 'border-l-amber-500 bg-amber-50/30' 
            : tipo === 'devuelta_por_socios'
              ? 'border-l-purple-500 bg-purple-50/30'
              : tipo === 'devuelta'
                ? 'border-l-red-500 bg-red-50/30' 
                : 'border-l-primary'
      }`}
      data-testid={`tarea-${tarea.id}`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <button 
                onClick={() => toggleTareaExpanded(tarea.id)}
                className="p-1 hover:bg-slate-100 rounded"
              >
                {expandedTareas[tarea.id] ? (
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-slate-500" />
                )}
              </button>
              {/* Badge de Prioridad con indicador visual */}
              {(() => {
                const priorityStyle = getPriorityStyle(tarea.priority);
                const PriorityIcon = priorityStyle.icon;
                return (
                  <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${priorityStyle.bgColor} ${priorityStyle.textColor} ${tarea.priority === 1 ? 'ring-2 ring-red-400 ring-offset-1' : ''}`}>
                    {PriorityIcon && <PriorityIcon className={`w-3 h-3 ${priorityStyle.iconColor}`} />}
                    {priorityStyle.label}
                  </span>
                );
              })()}
              <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusBadge(tarea.status)}`}>
                {tarea.status}
              </span>
              <span className="px-2 py-1 rounded text-xs bg-primary/10 text-primary">
                {tarea.area}
              </span>
              {isOverdue(tarea.deadline) && (
                <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-700 font-medium">
                  Vencida
                </span>
              )}
            </div>
            <h4 className="font-semibold text-slate-900 mb-1">{tarea.title}</h4>
            <p className="text-sm text-slate-600 mb-2">{tarea.description}</p>
            
            {/* Instrucción Inicial del Líder - Lo primero que debe ver el funcionario */}
            {tarea.comentario_inicial && (
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-3">
                <div className="flex items-start gap-2">
                  <div className="p-1.5 bg-blue-100 rounded-lg flex-shrink-0">
                    <MessageSquare className="w-4 h-4 text-blue-700" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-blue-800 mb-1">Instrucciones de la Jefatura:</p>
                    <p className="text-sm text-blue-700 whitespace-pre-wrap">{tarea.comentario_inicial}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Feedback del líder si fue devuelta */}
            {tarea.feedback_lider && (
              <div className="bg-red-100 border border-red-200 rounded-lg p-3 mb-3">
                <p className="text-sm font-medium text-red-800 mb-1">Feedback del Líder:</p>
                <p className="text-sm text-red-700">{tarea.feedback_lider}</p>
              </div>
            )}
            
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <span className={`flex items-center gap-1 ${isOverdue(tarea.deadline) ? 'text-red-600 font-medium' : ''}`}>
                <Calendar className="w-4 h-4" />
                Vence: {new Date(tarea.deadline).toLocaleDateString('es-CL')}
              </span>
              {tarea.gestiones?.length > 0 && (
                <span className="flex items-center gap-1 text-blue-600">
                  <MessageSquare className="w-4 h-4" />
                  {tarea.gestiones.length} gestiones
                </span>
              )}
              {getTotalAdjuntos(tarea) > 0 && (
                <span className="flex items-center gap-1 text-green-600 font-medium">
                  <Paperclip className="w-4 h-4" />
                  {getTotalAdjuntos(tarea)} documento{getTotalAdjuntos(tarea) !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2 ml-4">
            {/* Botón Ver Documentos - visible si hay adjuntos en la tarea */}
            {getTotalAdjuntos(tarea) > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="text-green-600 border-green-300 hover:bg-green-50"
                onClick={() => {
                  setSelectedTarea(tarea);
                  setShowVerDocumentos(true);
                }}
                data-testid={`ver-documentos-${tarea.id}`}
              >
                <Paperclip className="w-4 h-4 mr-1" />
                Ver Docs
              </Button>
            )}
            {/* Botón Ver Credenciales - visible si tiene cliente */}
            {tarea.client_id && (
              <Button
                variant="outline"
                size="sm"
                className="text-purple-600 border-purple-300 hover:bg-purple-50"
                onClick={() => {
                  setSelectedTarea(tarea);
                  setShowCredencialesTarea(true);
                }}
                data-testid={`ver-credenciales-${tarea.id}`}
              >
                <Key className="w-4 h-4 mr-1" />
                Credenciales
              </Button>
            )}
            {/* Acciones para tareas normales (no en revisión de socios ni devueltas por socios) */}
            {!['Revisión Socios', 'Devuelta por Socios'].includes(tarea.status) && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedTarea(tarea);
                    setShowGestion(true);
                  }}
                  data-testid={`registrar-gestion-${tarea.id}`}
                >
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Registrar
                </Button>
                <Button
                  size="sm"
                  className="bg-primary hover:bg-primary/90"
                  onClick={() => {
                    setSelectedTarea(tarea);
                    setShowCumplida(true);
                  }}
                  data-testid={`dar-cumplida-${tarea.id}`}
                >
                  <Send className="w-4 h-4 mr-1" />
                  Cumplida
                </Button>
              </>
            )}
            
            {/* Acciones para revisión de socios */}
            {tarea.status === 'Revisión Socios' && esSocio && (
              <Button
                size="sm"
                className="bg-amber-600 hover:bg-amber-700"
                onClick={() => {
                  setSelectedTarea(tarea);
                  setFeedbackSocio('');
                  setShowRevisarSocio(true);
                }}
                data-testid={`revisar-socio-${tarea.id}`}
              >
                <Star className="w-4 h-4 mr-1" />
                Revisar como Socio
              </Button>
            )}
            
            {/* Acciones para líder cuando el socio devuelve */}
            {tarea.status === 'Devuelta por Socios' && tarea.requiere_accion_lider && esLider && (
              <Button
                size="sm"
                className="bg-purple-600 hover:bg-purple-700"
                onClick={() => handleOpenGestionLider(tarea)}
                data-testid={`gestionar-devolucion-${tarea.id}`}
              >
                <AlertCircle className="w-4 h-4 mr-1" />
                Gestionar Devolución
              </Button>
            )}
            
            {/* Botón eliminar - NO mostrar si la tarea fue asignada por otro líder/socio */}
            {puedeEliminar && !tarea.no_eliminable && (
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                onClick={() => {
                  setTareaAEliminar(tarea);
                  setShowEliminar(true);
                }}
                data-testid={`eliminar-tarea-${tarea.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Bitácora de gestiones */}
        <AnimatePresence>
          {expandedTareas[tarea.id] && tarea.gestiones?.length > 0 && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="mt-4 pt-4 border-t border-slate-200"
            >
              <h5 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Bitácora de Gestiones
              </h5>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {tarea.gestiones.map((gestion, idx) => (
                  <div 
                    key={idx} 
                    className={`rounded-lg p-3 text-sm ${
                      gestion.tipo === 'aprobacion' 
                        ? 'bg-green-50 border border-green-200'
                        : gestion.tipo === 'rechazo'
                        ? 'bg-red-50 border border-red-200'
                        : gestion.tipo === 'completada'
                        ? 'bg-blue-50 border border-blue-200'
                        : 'bg-slate-50 border border-slate-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-slate-700">{gestion.usuario}</span>
                      <span className="text-xs text-slate-500">
                        {new Date(gestion.fecha).toLocaleString('es-CL')}
                      </span>
                    </div>
                    <p className="text-slate-600">{gestion.descripcion}</p>
                    {/* Mostrar adjuntos de la gestión */}
                    {gestion.attachments?.length > 0 && (
                      <GestionAttachments attachmentIds={gestion.attachments} />
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Card>
  );

  const renderGrupo = (grupo, idx, isDevuelta = false) => (
    <div key={idx} className="mb-4">
      {/* Elemento Padre: Cliente - Minuta/PSP */}
      <div 
        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-slate-100 ${
          isDevuelta ? 'bg-red-50' : 'bg-slate-50'
        }`}
        onClick={() => toggleGroupExpanded(isDevuelta ? `dev_${idx}` : idx)}
      >
        {expandedGroups[isDevuelta ? `dev_${idx}` : idx] ? (
          <ChevronDown className="w-5 h-5 text-slate-600" />
        ) : (
          <ChevronRight className="w-5 h-5 text-slate-600" />
        )}
        <Users className="w-5 h-5 text-primary" />
        <span className="font-semibold text-slate-800">
          {grupo.cliente}
        </span>
        <span className="text-slate-500">-</span>
        {grupo.meeting_id && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleViewPDF(grupo.meeting_id);
            }}
            className="flex items-center gap-1 text-primary hover:text-primary/80 hover:underline"
            title="Ver PDF de la Minuta"
          >
            <FileText className="w-4 h-4" />
            <span>Minuta</span>
          </button>
        )}
        {grupo.psp_folio && (
          <>
            <span className="text-slate-400">/</span>
            <span className="text-slate-600">PSP N°{grupo.psp_folio}</span>
          </>
        )}
        <span className={`ml-auto px-2 py-1 rounded text-xs font-medium ${
          isDevuelta ? 'bg-red-200 text-red-800' : 'bg-primary/20 text-primary'
        }`}>
          {grupo.tareas.length} tarea{grupo.tareas.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Tareas del grupo */}
      <AnimatePresence>
        {expandedGroups[isDevuelta ? `dev_${idx}` : idx] && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="space-y-2 mt-2"
          >
            {grupo.tareas.map(tarea => renderTareaCard(tarea, isDevuelta))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <div className="flex-1 p-8 overflow-y-auto max-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <ClipboardCheck className="w-10 h-10 text-primary" strokeWidth={1.5} />
                  <h1 className="text-4xl font-bold text-slate-900" data-testid="mis-tareas-title">
                    Mis Tareas Pendientes
                  </h1>
                </div>
                <p className="text-lg text-slate-600">
                  Gestiona y completa las tareas que te han sido asignadas
                </p>
              </div>
              <Button
                onClick={() => setShowCrearTarea(true)}
                className="bg-primary hover:bg-primary/90"
                data-testid="crear-tarea-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear Tarea
              </Button>
            </div>
          </div>

          {/* Tabs: Pendientes / Periódicas */}
          <div className="flex gap-2 mb-6 border-b border-slate-200">
            <button
              onClick={() => setActiveTab('pendientes')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'pendientes'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="tab-pendientes"
            >
              <Clock className="w-4 h-4 inline mr-2" />
              Tareas Pendientes
            </button>
            <button
              onClick={() => setActiveTab('periodicas')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'periodicas'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="tab-periodicas"
            >
              <RefreshCw className="w-4 h-4 inline mr-2" />
              Tareas Periódicas
              {tareasPeriodicasList.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-slate-200">
                  {tareasPeriodicasList.length}
                </span>
              )}
            </button>
          </div>

          {/* Contenido según pestaña activa */}
          {activeTab === 'pendientes' ? (
            <>
              {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="p-4 border-slate-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{tareasPendientes.length + tareasAsignadasPorLideres.length}</p>
                  <p className="text-sm text-slate-600">Pendientes</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border-slate-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <RotateCcw className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{tareasDevueltas.length}</p>
                  <p className="text-sm text-slate-600">Devueltas</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border-slate-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {tareas.filter(t => isOverdue(t.deadline)).length + periodicasVencidas.length}
                  </p>
                  <p className="text-sm text-slate-600">Vencidas</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Tareas Asignadas por Líderes/Socios */}
          {tareasAsignadasPorLideres.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <UserCheck className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-semibold text-purple-800">
                  Tareas Asignadas por Jefatura
                </h2>
                <span className="px-3 py-1 rounded-full text-sm font-bold bg-purple-100 text-purple-700">
                  {tareasAsignadasPorLideres.length}
                </span>
              </div>
              
              <div className="space-y-3">
                {tareasAsignadasPorLideres.map((tarea) => {
                  const prioridadStyle = getPriorityStyle(tarea.priority);
                  return (
                    <Card 
                      key={tarea.id} 
                      className="border-2 border-purple-300 bg-purple-50/30 hover:bg-purple-50 transition-colors"
                      data-testid={`tarea-lider-${tarea.id}`}
                    >
                      <div className="p-4">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          {/* Columna 1: Información de la tarea */}
                          <div className="lg:col-span-2 space-y-3">
                            {/* Quién te asignó */}
                            <div className="flex items-center gap-2 text-sm flex-wrap">
                              <span className="px-2 py-1 rounded bg-purple-200 text-purple-800 font-medium">
                                Asignada por {tarea.asignado_por}
                              </span>
                              <span className={`px-2 py-1 rounded ${prioridadStyle.bgColor} ${prioridadStyle.textColor} font-medium flex items-center gap-1`}>
                                {prioridadStyle.icon && <prioridadStyle.icon className="w-3 h-3" />}
                                {prioridadStyle.label}
                              </span>
                              <span className={`px-2 py-1 rounded ${
                                tarea.status === 'Pendiente' ? 'bg-amber-100 text-amber-700' :
                                tarea.status === 'En Progreso' ? 'bg-blue-100 text-blue-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {tarea.status}
                              </span>
                              <span className="px-2 py-1 rounded bg-slate-200 text-slate-700">
                                {tarea.area}
                              </span>
                            </div>
                            
                            {/* Título y descripción */}
                            <div className="bg-white border border-purple-200 rounded-lg p-3">
                              <p className="font-bold text-slate-900">{tarea.title}</p>
                              <p className="text-sm text-slate-600 mt-1 line-clamp-2">{tarea.description}</p>
                            </div>
                            
                            {/* Instrucciones de la Jefatura */}
                            {tarea.comentario_inicial && (
                              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                                <p className="text-xs text-purple-600 font-semibold mb-1">Instrucciones de la Jefatura:</p>
                                <p className="text-sm text-slate-800 italic">"{tarea.comentario_inicial}"</p>
                              </div>
                            )}
                          </div>
                          
                          {/* Columna 2: Datos del cliente y PSP */}
                          <div className="space-y-2">
                            {/* Cliente */}
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                              <p className="text-xs text-green-600 font-semibold">Cliente</p>
                              <p className="font-bold text-green-800">{tarea.client_name || 'N/A'}</p>
                            </div>
                            
                            {/* PSP/Folio */}
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                              <p className="text-xs text-purple-600 font-semibold">PSP / Folio</p>
                              <p className="font-bold text-purple-800">N° {tarea.psp_folio || 'Sin folio'}</p>
                            </div>
                            
                            {/* Vencimiento */}
                            <div className={`border rounded-lg p-3 ${
                              new Date(tarea.deadline) < new Date() 
                                ? 'bg-red-50 border-red-200' 
                                : 'bg-amber-50 border-amber-200'
                            }`}>
                              <p className={`text-xs font-semibold ${
                                new Date(tarea.deadline) < new Date() ? 'text-red-600' : 'text-amber-600'
                              }`}>Vencimiento</p>
                              <p className={`font-bold ${
                                new Date(tarea.deadline) < new Date() ? 'text-red-800' : 'text-amber-800'
                              }`}>
                                {new Date(tarea.deadline).toLocaleDateString('es-CL', {
                                  day: '2-digit', month: 'short', year: 'numeric'
                                })}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Nota de no eliminable */}
                        <div className="mt-4 pt-3 border-t border-purple-200">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <p className="text-xs text-purple-600 italic flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Esta tarea no puede ser eliminada. Gestiona directamente con quien te la asignó.
                            </p>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-purple-600 hover:text-purple-800"
                                onClick={() => {
                                  setSelectedTarea(tarea);
                                  setShowVerDocumentos(true);
                                }}
                              >
                                <Paperclip className="w-3 h-3 mr-1" />
                                Adjuntos
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-slate-600 hover:text-slate-800"
                                onClick={() => {
                                  setSelectedTarea(tarea);
                                  setShowCredencialesTarea(true);
                                }}
                              >
                                <Key className="w-3 h-3 mr-1" />
                                Credenciales
                              </Button>
                              <Button
                                size="sm"
                                className="bg-purple-600 hover:bg-purple-700 text-white"
                                onClick={() => {
                                  setSelectedTarea(tarea);
                                  setShowGestion(true);
                                }}
                              >
                                <MessageSquare className="w-3 h-3 mr-1" />
                                Registrar Gestión
                              </Button>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => {
                                  setSelectedTarea(tarea);
                                  setShowCumplida(true);
                                }}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Marcar Cumplida
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Referencias por Responder */}
          {referenciasPendientes.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-blue-800">
                  Referencias por Responder
                </h2>
                <span className="px-3 py-1 rounded-full text-sm font-bold bg-blue-100 text-blue-700">
                  {referenciasPendientes.length}
                </span>
              </div>
              
              <div className="space-y-3">
                {referenciasPendientes.map((ref) => (
                  <Card 
                    key={ref.id} 
                    className="border-2 border-blue-300 bg-blue-50/30 hover:bg-blue-50 transition-colors cursor-pointer"
                    onClick={() => handleOpenResponderReferencia(ref)}
                    data-testid={`referencia-${ref.id}`}
                  >
                    <div className="p-4">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {/* Columna 1: Información del contexto */}
                        <div className="lg:col-span-2 space-y-3">
                          {/* Quién te mencionó */}
                          <div className="flex items-center gap-2 text-sm">
                            <span className="px-2 py-1 rounded bg-blue-200 text-blue-800 font-medium">
                              @{ref.mencionado_por} te mencionó
                            </span>
                            <span className="text-slate-500">
                              {new Date(ref.created_at).toLocaleDateString('es-CL', {
                                day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                              })}
                            </span>
                          </div>
                          
                          {/* Comentario de origen - El texto exacto donde fue mencionado */}
                          <div className="bg-white border border-blue-200 rounded-lg p-3">
                            <p className="text-xs text-blue-600 font-semibold mb-1">Comentario donde fuiste mencionado:</p>
                            <p className="text-sm text-slate-800 italic">"{ref.mensaje_original}"</p>
                          </div>
                          
                          {/* Detalle de la tarea */}
                          {ref.task_info && (
                            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                              <p className="text-xs text-slate-500 font-semibold mb-1">Detalle de la Tarea:</p>
                              <p className="font-medium text-slate-800">{ref.task_info.task_title}</p>
                              <p className="text-sm text-slate-600 line-clamp-2">{ref.task_info.task_description}</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Columna 2: Datos del cliente y PSP */}
                        <div className="space-y-2">
                          {ref.task_info && (
                            <>
                              {/* Cliente */}
                              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <p className="text-xs text-green-600 font-semibold">Cliente</p>
                                <p className="font-bold text-green-800">{ref.task_info.client_name || 'N/A'}</p>
                              </div>
                              
                              {/* PSP/Folio */}
                              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                                <p className="text-xs text-purple-600 font-semibold">PSP / Folio</p>
                                <p className="font-bold text-purple-800">N° {ref.task_info.psp_folio || 'Sin folio'}</p>
                              </div>
                              
                              {/* Área */}
                              <div className="bg-slate-100 border border-slate-200 rounded-lg p-3">
                                <p className="text-xs text-slate-500 font-semibold">Área</p>
                                <p className="font-medium text-slate-700">{ref.task_info.area || 'N/A'}</p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Botones de acción */}
                      <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 border-t border-blue-200">
                        {/* Botones de ver información */}
                        <div className="flex flex-wrap gap-2">
                          {ref.task_info && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-blue-600 hover:text-blue-800 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVerMinutaRef(ref);
                                }}
                              >
                                <FileText className="w-3 h-3 mr-1" />
                                Ver Minutas PSP
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-slate-600 hover:text-slate-800 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVerBitacoraRef(ref);
                                }}
                              >
                                <MessageSquare className="w-3 h-3 mr-1" />
                                Ver Bitácora
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-amber-600 hover:text-amber-700 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVerCredencialesRef(ref);
                                }}
                              >
                                <Key className="w-3 h-3 mr-1" />
                                Credenciales
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-green-600 hover:text-green-700 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVerAdjuntosRef(ref);
                                }}
                              >
                                <Paperclip className="w-3 h-3 mr-1" />
                                Adjuntos
                              </Button>
                            </>
                          )}
                        </div>
                        
                        {/* Botón principal de responder */}
                        <Button
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenResponderReferencia(ref);
                          }}
                          data-testid={`responder-ref-${ref.id}`}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Responder Referencia
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </motion.div>
          )}

          {/* Tareas Periódicas Vencidas */}
          {periodicasVencidas.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h2 className="text-xl font-semibold text-red-800">
                  Tareas Periódicas Vencidas
                </h2>
                <span className="px-3 py-1 rounded-full text-sm font-bold bg-red-100 text-red-700">
                  {periodicasVencidas.length}
                </span>
              </div>
              
              <div className="space-y-3">
                {periodicasVencidas.map((tarea) => (
                  <Card 
                    key={tarea.id} 
                    className="border-2 border-red-300 bg-red-50/30 hover:bg-red-50 transition-colors"
                    data-testid={`periodica-vencida-${tarea.id}`}
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <RefreshCw className="w-4 h-4 text-red-600" />
                            <h3 className="font-semibold text-red-800">{tarea.titulo}</h3>
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-200 text-red-800">
                              VENCIDA
                            </span>
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-200 text-slate-700">
                              {getFrecuenciaLabel(tarea.frecuencia)}
                            </span>
                          </div>
                          {tarea.descripcion && (
                            <p className="text-sm text-slate-600 mb-2">{tarea.descripcion}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                            <span className="flex items-center gap-1 text-red-600 font-medium">
                              <Clock className="w-3.5 h-3.5" />
                              Venció: {new Date(tarea.proxima_ejecucion).toLocaleDateString('es-CL')}
                            </span>
                            <span>Ciclo {tarea.repeticiones_completadas + 1} de {tarea.repeticiones_total}</span>
                            {tarea.client_name && (
                              <span className="flex items-center gap-1">
                                <Briefcase className="w-3.5 h-3.5" />
                                {tarea.client_name}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 ml-4"
                          onClick={() => {
                            setPeriodicaSeleccionada(tarea);
                            setShowCumplirPeriodica(true);
                          }}
                          data-testid={`cumplir-vencida-${tarea.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Completar Ahora
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </motion.div>
          )}

          {/* Tareas en Revisión de Socios (visible solo para socios) */}
          {esSocio && gruposRevisionSocios.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-amber-700 mb-4 flex items-center gap-2">
                <Star className="w-5 h-5" />
                Revisión Directiva - Tareas Escaladas
                <span className="ml-2 px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-800 font-bold">
                  PRIORIDAD ALTA
                </span>
              </h2>
              {gruposRevisionSocios.map((grupo, idx) => (
                <div key={`rev_${idx}`} className="mb-4">
                  <div 
                    className="flex items-center justify-between p-3 rounded-lg cursor-pointer bg-amber-50"
                    onClick={() => toggleGroupExpanded(`rev_${idx}`)}
                  >
                    {expandedGroups[`rev_${idx}`] ? (
                      <ChevronDown className="w-5 h-5 text-amber-600" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-amber-600" />
                    )}
                    <div className="flex-1 ml-3">
                      <p className="font-semibold text-slate-800">{grupo.cliente}</p>
                      <p className="text-sm text-slate-600">
                        {grupo.proposal_id ? `PSP N° ${grupo.psp_folio || 'Sin folio'}` : 'Sin PSP asociada'}
                      </p>
                    </div>
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-amber-200 text-amber-800">
                      {grupo.tareas.length} tarea(s)
                    </span>
                  </div>
                  {expandedGroups[`rev_${idx}`] && (
                    <div className="space-y-3 mt-3">
                      {grupo.tareas.map(tarea => renderTareaCard(tarea, false, true))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Tareas Devueltas por Socios - Para Líderes */}
          {esLider && gruposDevueltasPorSocios.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-purple-700 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Devueltas por Socios - Requieren Tu Gestión
                <span className="ml-2 px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800 font-bold">
                  ACCIÓN REQUERIDA
                </span>
              </h2>
              {gruposDevueltasPorSocios.map((grupo, idx) => (
                <div key={`dev_socio_${idx}`} className="mb-4">
                  <div 
                    className="flex items-center justify-between p-3 rounded-lg cursor-pointer bg-purple-50"
                    onClick={() => toggleGroupExpanded(`dev_socio_${idx}`)}
                  >
                    {expandedGroups[`dev_socio_${idx}`] ? (
                      <ChevronDown className="w-5 h-5 text-purple-600" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-purple-600" />
                    )}
                    <div className="flex-1 ml-3">
                      <p className="font-semibold text-slate-800">{grupo.cliente}</p>
                      <p className="text-sm text-slate-600">
                        {grupo.proposal_id ? `PSP N° ${grupo.psp_folio || 'Sin folio'}` : 'Sin PSP asociada'}
                      </p>
                    </div>
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-200 text-purple-800">
                      {grupo.tareas.length} tarea(s)
                    </span>
                  </div>
                  {expandedGroups[`dev_socio_${idx}`] && (
                    <div className="space-y-3 mt-3">
                      {grupo.tareas.map(tarea => renderTareaCard(tarea, 'devuelta_por_socios'))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Tareas Devueltas */}
          {gruposDevueltas.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-red-700 mb-4 flex items-center gap-2">
                <RotateCcw className="w-5 h-5" />
                Tareas Devueltas - Requieren Atención
              </h2>
              {gruposDevueltas.map((grupo, idx) => renderGrupo(grupo, idx, true))}
            </div>
          )}

          {/* Tareas Pendientes */}
          {loading ? (
            <Card className="p-12 text-center border-slate-200">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-600">Cargando tareas...</p>
            </Card>
          ) : gruposPendientes.length === 0 && gruposDevueltas.length === 0 && gruposRevisionSocios.length === 0 ? (
            <Card className="p-12 text-center border-slate-200">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-lg text-slate-600 mb-2">¡No tienes tareas pendientes!</p>
              <p className="text-sm text-slate-500">
                Las tareas asignadas aparecerán aquí organizadas por cliente y proyecto
              </p>
            </Card>
          ) : gruposPendientes.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">
                Tareas Activas ({tareasPendientes.length})
              </h2>
              {gruposPendientes.map((grupo, idx) => renderGrupo(grupo, idx, false))}
            </div>
          )}
            </>
          ) : (
            /* ==================== TAB TAREAS PERIÓDICAS ==================== */
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Header con botón crear */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <RefreshCw className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-slate-800">Mis Tareas Periódicas</h2>
                    <p className="text-sm text-slate-500">Tareas que se repiten automáticamente</p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowCrearPeriodica(true)}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="nueva-periodica-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Tarea Periódica
                </Button>
              </div>

              {/* Lista de tareas periódicas */}
              {loadingPeriodicas ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full" />
                </div>
              ) : tareasPeriodicasList.length === 0 ? (
                <Card className="p-12 text-center border-dashed border-2 border-slate-200">
                  <RefreshCw className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-medium text-slate-600 mb-2">Sin Tareas Periódicas</h3>
                  <p className="text-slate-500 mb-4">Crea tareas que se repitan diaria, semanal o mensualmente</p>
                  <Button
                    onClick={() => setShowCrearPeriodica(true)}
                    variant="outline"
                    className="border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Crear Primera Tarea
                  </Button>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {tareasPeriodicasList.map((tarea) => (
                    <Card 
                      key={tarea.id} 
                      className={`p-4 border-2 transition-all hover:shadow-md ${
                        tarea.estado === 'pausada' ? 'border-yellow-200 bg-yellow-50/30' :
                        tarea.estado === 'completada' ? 'border-blue-200 bg-blue-50/30' :
                        'border-slate-200 hover:border-indigo-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-slate-800">{tarea.titulo}</h3>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getEstadoColor(tarea.estado)}`}>
                              {tarea.estado}
                            </span>
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700">
                              {getFrecuenciaLabel(tarea.frecuencia)}
                            </span>
                          </div>
                          {tarea.descripcion && (
                            <p className="text-sm text-slate-600 mb-2">{tarea.descripcion}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              Próxima: {new Date(tarea.proxima_ejecucion).toLocaleDateString('es-CL')}
                            </span>
                            <span className="flex items-center gap-1">
                              <RefreshCw className="w-3.5 h-3.5" />
                              {tarea.repeticiones_completadas} / {tarea.repeticiones_total} ciclos
                            </span>
                            {tarea.client_name && (
                              <span className="flex items-center gap-1">
                                <Briefcase className="w-3.5 h-3.5" />
                                {tarea.client_name}
                              </span>
                            )}
                            {tarea.asignado_name !== tarea.creador_name && (
                              <span className="flex items-center gap-1 text-amber-600">
                                <Users className="w-3.5 h-3.5" />
                                Asignada a: {tarea.asignado_name}
                              </span>
                            )}
                          </div>
                          {/* Barra de progreso */}
                          <div className="mt-3">
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div 
                                className="bg-indigo-500 h-2 rounded-full transition-all"
                                style={{ width: `${(tarea.repeticiones_completadas / tarea.repeticiones_total) * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* Acciones */}
                        <div className="flex items-center gap-2 ml-4">
                          {tarea.estado === 'activa' && tarea.asignado_id === currentUser?.id && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => {
                                setPeriodicaSeleccionada(tarea);
                                setShowCumplirPeriodica(true);
                              }}
                              data-testid={`cumplir-periodica-${tarea.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Cumplir
                            </Button>
                          )}
                          {tarea.historial?.length > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setPeriodicaSeleccionada(tarea);
                                setShowHistorialPeriodica(true);
                              }}
                              data-testid={`historial-periodica-${tarea.id}`}
                            >
                              <History className="w-4 h-4 mr-1" />
                              Historial
                            </Button>
                          )}
                          {tarea.creador_id === currentUser?.id && tarea.estado !== 'completada' && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={tarea.estado === 'pausada' ? 'text-green-600 hover:bg-green-50' : 'text-yellow-600 hover:bg-yellow-50'}
                                onClick={() => handlePausarPeriodica(tarea)}
                                title={tarea.estado === 'pausada' ? 'Reactivar' : 'Pausar'}
                              >
                                {tarea.estado === 'pausada' ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleEliminarPeriodica(tarea)}
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Modal Registrar Gestión */}
      <Dialog open={showGestion} onOpenChange={(open) => {
        setShowGestion(open);
        if (!open) setMencionesGestion([]);
      }}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white">
          <DialogHeader>
            <DialogTitle className='text-lg font-semibold text-slate-900'>Registrar Gestión en Bitácora</DialogTitle>
            <DialogDescription className='text-sm text-slate-500'>
              Este registro quedará guardado cronológicamente para auditoría. Usa @ para mencionar colegas.
            </DialogDescription>
          </DialogHeader>
          {selectedTarea && (
            <div className="mt-4">
              {/* Detalle de la tarea */}
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-slate-800">{selectedTarea.title}</p>
                <p className="text-xs text-slate-600 mt-1">{selectedTarea.description}</p>
              </div>

              {/* Layout de 2 columnas */}
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {/* Columna Izquierda: Documentos existentes */}
                <div className="space-y-4">
                  <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
                    <p className="text-sm font-medium text-slate-700 mb-3">Documentos de la Tarea</p>
                    <TaskAttachments
                      entityType="task"
                      entityId={selectedTarea.id}
                      attachments={selectedTarea.attachments || []}
                      canUpload={false}
                      showBitacoraAttachments={true}
                    />
                  </div>
                </div>

                {/* Columna Derecha: Descripción y Adjuntos */}
                <div className="space-y-4">
                  <div>
                    <Label className='text-sm font-medium mb-2 block'>Descripción de la gestión</Label>
                    <MentionInput
                      value={gestionText}
                      onChange={setGestionText}
                      onMentionsChange={setMencionesGestion}
                      placeholder="Describe la gestión realizada... Usa @ para mencionar colegas"
                      rows={4}
                      data-testid="gestion-input"
                    />
                  </div>
                  
                  {/* Adjuntar archivos */}
                  <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Paperclip className="w-4 h-4 text-blue-600" />
                      <Label className="text-sm font-medium text-blue-800">Adjuntar Avances Parciales</Label>
                    </div>
                    <p className="text-xs text-blue-600 mb-3">
                      Puede adjuntar documentos que respalden esta gestión
                    </p>
                    <TaskAttachments
                      entityType="task"
                      entityId={selectedTarea.id}
                      attachments={selectedTarea.attachments || []}
                      onAttachmentsChange={(newAttachments) => {
                        setSelectedTarea({ ...selectedTarea, attachments: newAttachments });
                      }}
                      canUpload={true}
                    />
                  </div>
                </div>
              </div>

              {/* Botón de acción alineado a la derecha */}
              <div className='flex justify-end mt-6 pt-4 border-t border-slate-100'>
                <Button 
                  onClick={handleRegistrarGestion} 
                  className="bg-[#1E3649] hover:bg-[#2A4A5F] text-white px-6"
                  disabled={submitting || !gestionText.trim()}
                  data-testid="confirmar-gestion-btn"
                >
                  {submitting ? 'Registrando...' : 'Guardar en Bitácora'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Dar por Cumplida */}
      <Dialog open={showCumplida} onOpenChange={setShowCumplida}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white">
          <DialogHeader>
            <DialogTitle className='text-lg font-semibold text-slate-900'>Dar por Cumplida</DialogTitle>
            <DialogDescription className='text-sm text-slate-500'>
              La tarea será enviada a revisión del líder de área
            </DialogDescription>
          </DialogHeader>
          {selectedTarea && (
            <div className="mt-4">
              {/* Detalle de la tarea */}
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-slate-800">{selectedTarea.title}</p>
                <p className="text-xs text-slate-600 mt-1">{selectedTarea.description}</p>
              </div>

              {/* Layout de 2 columnas */}
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {/* Columna Izquierda: Documentos y Credenciales */}
                <div className="space-y-4">
                  {/* Sección de Credenciales del Cliente */}
                  {selectedTarea.client_id && (
                    <ClientCredentials
                      clientId={selectedTarea.client_id}
                      clientName={selectedTarea.client_name || 'Cliente'}
                      canEdit={true}
                    />
                  )}
                  
                  {/* Sección de Archivos Adjuntos */}
                  <div className="border border-slate-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-slate-700 mb-3">Documentos y Entregables</p>
                    <TaskAttachments
                      entityType="task"
                      entityId={selectedTarea.id}
                      attachments={selectedTarea.attachments || []}
                      onAttachmentsChange={(newAttachments) => {
                        setSelectedTarea({ ...selectedTarea, attachments: newAttachments });
                      }}
                      canUpload={true}
                      showBitacoraAttachments={true}
                    />
                  </div>
                </div>

                {/* Columna Derecha: Comentario de Cierre */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      Comentario de Cierre <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                      value={comentarioCumplida}
                      onChange={(e) => setComentarioCumplida(e.target.value)}
                      placeholder="Detalle lo realizado para completar esta tarea (obligatorio para auditoría)..."
                      rows={6}
                      className={`resize-none ${!comentarioCumplida.trim() ? 'border-red-300 focus:border-red-400' : 'border-slate-200'}`}
                      data-testid="cumplida-input"
                    />
                    {!comentarioCumplida.trim() && (
                      <p className="text-xs text-red-500 mt-1">Este campo es obligatorio</p>
                    )}
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      Al confirmar, la tarea y su bitácora completa serán enviadas a tu líder para revisión.
                    </p>
                  </div>
                </div>
              </div>

              {/* Botón de acción alineado a la derecha */}
              <div className='flex justify-end mt-6 pt-4 border-t border-slate-100'>
                <Button 
                  onClick={handleDarPorCumplida} 
                  className="bg-green-600 hover:bg-green-700 text-white px-6"
                  disabled={submitting || !comentarioCumplida.trim()}
                  data-testid="confirmar-cumplida-btn"
                >
                  {submitting ? 'Enviando...' : 'Confirmar y Enviar a Revisión'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Crear Tarea */}
      <Dialog open={showCrearTarea} onOpenChange={(open) => {
        setShowCrearTarea(open);
        if (!open) {
          setSelectedPsp(null);
          setPspSearch('');
        }
      }}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white">
          <DialogHeader>
            <DialogTitle className='text-lg font-semibold text-slate-900'>Crear Nueva Tarea</DialogTitle>
            <DialogDescription className='text-sm text-slate-500'>
              Toda tarea debe estar vinculada a un Cliente/PSP para mantener la trazabilidad
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {/* Layout de 2 columnas */}
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
              {/* Columna Izquierda: PSP y Detalles */}
              <div className="space-y-4">
                {/* Selector de PSP con búsqueda */}
                <div className="space-y-2">
                  <Label className='text-sm font-medium'>Cliente / PSP <span className="text-red-500">*</span></Label>
                  {selectedPsp ? (
                    <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                      <Briefcase className="w-5 h-5 text-[#1E3649]" />
                      <div className="flex-1">
                        <p className="font-medium text-sm text-slate-900">{selectedPsp.client_name}</p>
                        <p className="text-xs text-slate-600">PSP {selectedPsp.folio}: {selectedPsp.titulo?.slice(0, 40)}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => {
                          setSelectedPsp(null);
                          setNuevaTarea({ ...nuevaTarea, proposal_id: '' });
                        }}
                        className="text-slate-500 hover:text-slate-700"
                      >
                        Cambiar
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          value={pspSearch}
                          onChange={(e) => setPspSearch(e.target.value)}
                          placeholder="Buscar por cliente, folio o título de PSP..."
                          className="pl-10"
                          data-testid="psp-search-input"
                        />
                      </div>
                      <div className="border rounded-lg max-h-32 overflow-y-auto">
                        {loadingPsps ? (
                          <div className="p-4 text-center text-slate-500 text-sm">
                            Cargando PSPs activas...
                          </div>
                        ) : pspsActivas.length === 0 ? (
                          <div className="p-4 text-center text-slate-500 text-sm">
                            {pspSearch ? 'No se encontraron PSPs con esa búsqueda' : 'No hay PSPs activas disponibles'}
                          </div>
                        ) : (
                          pspsActivas.map((psp) => (
                            <button
                              key={psp.id}
                              onClick={() => handleSelectPsp(psp)}
                              className="w-full text-left p-3 hover:bg-slate-50 border-b last:border-b-0 transition-colors"
                              data-testid={`psp-option-${psp.id}`}
                            >
                              <p className="font-medium text-sm text-slate-900">{psp.client_name}</p>
                              <p className="text-xs text-slate-500">PSP {psp.folio}: {psp.titulo?.slice(0, 50)}</p>
                            </button>
                          ))
                        )}
                      </div>
                      {!nuevaTarea.proposal_id && (
                        <p className="text-xs text-amber-600 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Seleccione una PSP para vincular la tarea
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Título */}
                <div className="space-y-2">
                  <Label className='text-sm font-medium'>Título de la Tarea <span className="text-red-500">*</span></Label>
                  <Input
                    value={nuevaTarea.title}
                    onChange={(e) => setNuevaTarea({ ...nuevaTarea, title: e.target.value })}
                    placeholder="Ej: Revisar declaración de impuestos"
                    data-testid="nueva-tarea-titulo"
                  />
                </div>

                {/* Descripción */}
                <div className="space-y-2">
                  <Label className='text-sm font-medium'>Descripción <span className="text-red-500">*</span></Label>
                  <Textarea
                    value={nuevaTarea.description}
                    onChange={(e) => setNuevaTarea({ ...nuevaTarea, description: e.target.value })}
                    placeholder="Describe detalladamente la tarea a realizar..."
                    rows={4}
                    className='resize-none'
                    data-testid="nueva-tarea-descripcion"
                  />
                </div>
              </div>

              {/* Columna Derecha: Área y Fecha */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className='text-sm font-medium'>Área <span className="text-red-500">*</span></Label>
                  {/* Si el usuario tiene rol "Usuario" o "Líder", mostrar su área fija sin poder cambiarla */}
                  {(currentUser?.role === 'Usuario' || currentUser?.role === 'Líder') ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={nuevaTarea.area || currentUser?.areas?.[0] || ''}
                        disabled
                        className="bg-slate-100 cursor-not-allowed"
                        data-testid="nueva-tarea-area-fijo"
                      />
                      <span className="text-xs text-slate-500">(Área asignada)</span>
                    </div>
                  ) : (
                    <Select
                      value={nuevaTarea.area}
                      onValueChange={(value) => setNuevaTarea({ ...nuevaTarea, area: value })}
                    >
                      <SelectTrigger data-testid="nueva-tarea-area">
                        <SelectValue placeholder="Seleccionar área" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Tributaria">Tributaria</SelectItem>
                        <SelectItem value="Legal">Legal</SelectItem>
                        <SelectItem value="Contabilidad">Contabilidad</SelectItem>
                        <SelectItem value="Comercial">Comercial</SelectItem>
                        <SelectItem value="Adm. y Finanzas">Adm. y Finanzas</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
                
                {/* Selector de Prioridad */}
                <div className="space-y-2">
                  <Label className='text-sm font-medium'>Prioridad</Label>
                  <Select
                    value={String(nuevaTarea.priority)}
                    onValueChange={(value) => setNuevaTarea({ ...nuevaTarea, priority: parseInt(value) })}
                  >
                    <SelectTrigger data-testid="nueva-tarea-prioridad">
                      <SelectValue placeholder="Seleccionar prioridad" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">
                        <span className="flex items-center gap-2">
                          <Flame className="w-4 h-4 text-red-500" />
                          <span>1 - Urgente (Prioritaria)</span>
                        </span>
                      </SelectItem>
                      <SelectItem value="2">
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 flex items-center justify-center text-blue-600 font-bold">●</span>
                          <span>2 - Regular (Normal)</span>
                        </span>
                      </SelectItem>
                      <SelectItem value="3">
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 flex items-center justify-center text-slate-400 font-bold">○</span>
                          <span>3 - Baja (Programable)</span>
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Por defecto: Regular. Solo cambie si requiere urgencia o es de baja prioridad.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label className='text-sm font-medium'>Fecha de Vencimiento <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    value={nuevaTarea.deadline}
                    onChange={(e) => setNuevaTarea({ ...nuevaTarea, deadline: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="nueva-tarea-deadline"
                  />
                </div>
              </div>
            </div>

            {/* Botón de acción alineado a la derecha */}
            <div className='flex justify-end mt-6 pt-4 border-t border-slate-100'>
              <Button 
                onClick={handleCrearTarea} 
                className="bg-[#1E3649] hover:bg-[#2A4A5F] text-white px-6"
                disabled={submitting || !nuevaTarea.title || !nuevaTarea.description || !nuevaTarea.area || !nuevaTarea.deadline || !nuevaTarea.proposal_id}
                data-testid="confirmar-crear-tarea-btn"
              >
                {submitting ? 'Creando...' : 'Crear Tarea'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Ver Documentos de la Tarea */}
      <Dialog open={showVerDocumentos} onOpenChange={setShowVerDocumentos}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1E3649] text-lg font-semibold">
              <Paperclip className="w-5 h-5" />
              Documentos de la Tarea
            </DialogTitle>
            <DialogDescription className='text-sm text-slate-500'>
              Todos los documentos adjuntados a esta tarea por el equipo
            </DialogDescription>
          </DialogHeader>
          {selectedTarea && (
            <div className="mt-4">
              {/* Detalle de la tarea */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-slate-800">{selectedTarea.title}</p>
                <p className="text-xs text-slate-600 mt-1">{selectedTarea.description?.slice(0, 150)}...</p>
              </div>
              
              {/* Componente de Adjuntos con historial de bitácora */}
              <TaskAttachments
                entityType="task"
                entityId={selectedTarea.id}
                attachments={selectedTarea.attachments || []}
                canUpload={false}
                showBitacoraAttachments={true}
              />
              
              {/* Botón alineado a la derecha */}
              <div className='flex justify-end mt-6 pt-4 border-t border-slate-100'>
                <Button 
                  onClick={() => setShowVerDocumentos(false)}
                  variant="outline"
                  className='px-6'
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Confirmar Eliminación */}
      <Dialog open={showEliminar} onOpenChange={setShowEliminar}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Eliminar Tarea
            </DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer. ¿Está seguro de eliminar esta tarea?
            </DialogDescription>
          </DialogHeader>
          {tareaAEliminar && (
            <div className="mt-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-medium text-slate-900">{tareaAEliminar.title}</p>
                <p className="text-sm text-slate-600 mt-1">{tareaAEliminar.description?.slice(0, 100)}</p>
                <div className="flex gap-4 mt-2 text-xs text-slate-500">
                  <span>Área: {tareaAEliminar.area}</span>
                  <span>Responsable: {tareaAEliminar.responsible}</span>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowEliminar(false);
                    setTareaAEliminar(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleEliminarTarea}
                  disabled={eliminando}
                  data-testid="confirmar-eliminar-btn"
                >
                  {eliminando ? 'Eliminando...' : 'Eliminar Tarea'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Revisar como Socio */}
      <Dialog open={showRevisarSocio} onOpenChange={(open) => {
        setShowRevisarSocio(open);
        if (!open) setAdjuntosSocio([]);
      }}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600 text-lg font-semibold">
              <Star className="w-5 h-5" />
              Revisión Directiva
            </DialogTitle>
            <DialogDescription className='text-sm text-slate-500'>
              Como socio, puede aprobar o devolver esta tarea con feedback visual
            </DialogDescription>
          </DialogHeader>
          {selectedTarea && (
            <div className="mt-4">
              {/* Detalle de la tarea */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="font-medium text-slate-900">{selectedTarea.title}</p>
                <p className="text-sm text-slate-600 mt-1">{selectedTarea.description?.slice(0, 150)}...</p>
                <div className="flex gap-4 mt-2 text-xs text-slate-500">
                  <span>Área: {selectedTarea.area}</span>
                  <span>Responsable: {selectedTarea.responsible}</span>
                </div>
                {selectedTarea.comentario_escalamiento && (
                  <div className="mt-3 p-3 bg-white rounded border border-amber-200">
                    <p className="text-xs text-amber-700 font-medium">Motivo del escalamiento:</p>
                    <p className="text-sm text-slate-700 mt-1">{selectedTarea.comentario_escalamiento}</p>
                    <p className="text-xs text-slate-500 mt-1">Por: {selectedTarea.escalado_por}</p>
                  </div>
                )}
              </div>

              {/* Layout de 2 columnas */}
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {/* Columna Izquierda: Documentos existentes */}
                <div className="space-y-4">
                  <div className="border border-slate-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-slate-700 mb-3">Documentos de la Tarea</p>
                    <TaskAttachments
                      entityType="task"
                      entityId={selectedTarea.id}
                      attachments={selectedTarea.attachments || []}
                      canUpload={false}
                      showBitacoraAttachments={true}
                    />
                  </div>
                </div>

                {/* Columna Derecha: Feedback y Adjuntos */}
                <div className="space-y-4">
                  {/* Adjuntos del Socio */}
                  <div className="border border-amber-200 rounded-lg p-4 bg-amber-50/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Paperclip className="w-4 h-4 text-amber-600" />
                      <Label className="text-sm font-medium text-amber-800">Adjuntar Feedback</Label>
                    </div>
                    <p className="text-xs text-amber-600 mb-3">
                      Documentos con anotaciones, correcciones o instrucciones
                    </p>
                    <TaskAttachments
                      entityType="task_review_socio"
                      entityId={selectedTarea.id}
                      attachments={adjuntosSocio}
                      onAttachmentsChange={setAdjuntosSocio}
                      canUpload={true}
                    />
                  </div>
                  
                  {/* Feedback */}
                  <div>
                    <Label className='text-sm font-medium mb-2 block'>
                      Feedback de revisión <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      value={feedbackSocio}
                      onChange={(e) => setFeedbackSocio(e.target.value)}
                      placeholder="Ingrese su feedback como socio..."
                      rows={4}
                      className='resize-none'
                      data-testid="feedback-socio"
                    />
                  </div>
                </div>
              </div>
              
              {/* Botones de acción alineados a la derecha */}
              <div className='flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100'>
                <Button
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50 px-4"
                  onClick={handleDevolverSocio}
                  disabled={!feedbackSocio.trim() || submittingSocio}
                  data-testid="devolver-socio-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Devolver
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700 px-6"
                  onClick={handleAprobarSocio}
                  disabled={!feedbackSocio.trim() || submittingSocio}
                  data-testid="aprobar-socio-btn"
                >
                  <ThumbsUp className="w-4 h-4 mr-1" />
                  Aprobar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Gestión de Líder para Tareas Devueltas por Socios */}
      <Dialog open={showGestionLider} onOpenChange={(open) => {
        setShowGestionLider(open);
        if (!open) setAdjuntosGestionLider([]);
      }}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1E3649] text-lg font-semibold">
              <AlertCircle className="w-5 h-5" />
              Gestionar Devolución de Socio
            </DialogTitle>
            <DialogDescription className='text-sm text-slate-500'>
              Elija cómo proceder con esta tarea devuelta
            </DialogDescription>
          </DialogHeader>
          {selectedTarea && (
            <div className="mt-4">
              {/* Detalle de la tarea */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                <p className="font-medium text-slate-900">{selectedTarea.title}</p>
                <p className="text-sm text-slate-600 mt-1">{selectedTarea.description?.slice(0, 100)}...</p>
                {selectedTarea.feedback_socio && (
                  <div className="mt-3 p-3 bg-white rounded border border-purple-200">
                    <p className="text-xs text-purple-700 font-medium">Observaciones del Socio:</p>
                    <p className="text-sm text-slate-700 mt-1">{selectedTarea.feedback_socio}</p>
                    <p className="text-xs text-slate-500 mt-1">Devuelta por: {selectedTarea.devuelto_por_socio}</p>
                  </div>
                )}
              </div>

              {/* Layout de 2 columnas */}
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {/* Columna Izquierda: Documentos y Selector */}
                <div className="space-y-4">
                  {/* Documentos existentes */}
                  {getTotalAdjuntos(selectedTarea) > 0 && (
                    <div className="border border-slate-200 rounded-lg p-4">
                      <p className="text-sm font-medium text-slate-700 mb-3">Documentos del proceso ({getTotalAdjuntos(selectedTarea)})</p>
                      <TaskAttachments
                        entityType="task"
                        entityId={selectedTarea.id}
                        attachments={selectedTarea.attachments || []}
                        canUpload={false}
                        showBitacoraAttachments={true}
                      />
                    </div>
                  )}
                  
                  {/* Selector de acción */}
                  <div className="space-y-3">
                    <Label className='text-sm font-medium'>¿Cómo desea proceder?</Label>
                    <div className="grid grid-cols-1 gap-3">
                      <button
                        type="button"
                        onClick={() => setAccionLider('auto_resolver')}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          accionLider === 'auto_resolver' 
                            ? 'border-[#1E3649] bg-[#1E3649]/5' 
                            : 'border-slate-200 hover:border-[#1E3649]/30'
                        }`}
                        data-testid="opcion-auto-resolver"
                      >
                        <UserCheck className="w-5 h-5 text-[#1E3649] mb-1" />
                        <p className="font-medium text-sm">Auto-resolver</p>
                        <p className="text-xs text-slate-500">Corrijo yo mismo y reenvío al socio</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setAccionLider('derivar')}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          accionLider === 'derivar' 
                            ? 'border-[#1E3649] bg-[#1E3649]/5' 
                            : 'border-slate-200 hover:border-[#1E3649]/30'
                        }`}
                        data-testid="opcion-derivar"
                      >
                        <ArrowRight className="w-5 h-5 text-[#1E3649] mb-1" />
                        <p className="font-medium text-sm">Derivar</p>
                        <p className="text-xs text-slate-500">Reasignar a funcionario para corrección</p>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Columna Derecha: Formulario según acción */}
                <div className="space-y-4">
                  {/* Campos según acción - Auto-resolver */}
                  {accionLider === 'auto_resolver' && (
                    <div className="space-y-4 p-4 border border-slate-200 rounded-lg bg-slate-50/50">
                      <div className="space-y-2">
                        <Label className='text-sm font-medium'>Comentario de resolución <span className="text-red-500">*</span></Label>
                        <Textarea
                          value={comentarioLider}
                          onChange={(e) => setComentarioLider(e.target.value)}
                          placeholder="Describa las correcciones realizadas..."
                          rows={3}
                          className='resize-none'
                          data-testid="comentario-auto-resolver"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className='text-sm font-medium'>Adjuntar documentos corregidos</Label>
                        <TaskAttachments
                          entityType="task"
                          entityId={selectedTarea.id}
                          attachments={adjuntosGestionLider}
                          onAttachmentsChange={setAdjuntosGestionLider}
                          canUpload={true}
                          compact={true}
                        />
                        <p className="text-xs text-slate-500">Suba los documentos corregidos para enviar al Socio</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Campos según acción - Derivar */}
                  {accionLider === 'derivar' && (
                    <div className="space-y-4 p-4 border border-slate-200 rounded-lg bg-slate-50/50">
                      <div className="space-y-2">
                        <Label className='text-sm font-medium'>Funcionario asignado <span className="text-red-500">*</span></Label>
                        <Select
                          value={funcionarioDerivacion.id}
                          onValueChange={(value) => {
                            const usuario = usuariosEquipo.find(u => u.id === value);
                            setFuncionarioDerivacion({ id: value, name: usuario?.name || '' });
                          }}
                        >
                          <SelectTrigger data-testid="select-funcionario">
                            <SelectValue placeholder="Seleccionar funcionario" />
                          </SelectTrigger>
                          <SelectContent>
                            {usuariosEquipo.map(u => (
                              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className='text-sm font-medium'>Nuevo plazo <span className="text-red-500">*</span></Label>
                        <Input
                          type="date"
                          value={plazoDerivacion}
                          onChange={(e) => setPlazoDerivacion(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          data-testid="plazo-derivacion"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className='text-sm font-medium'>Instrucciones <span className="text-red-500">*</span></Label>
                        <Textarea
                          value={comentarioLider}
                          onChange={(e) => setComentarioLider(e.target.value)}
                          placeholder="Indique qué correcciones debe realizar..."
                          rows={3}
                          className='resize-none'
                          data-testid="comentario-derivar"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className='text-sm font-medium'>Adjuntar documentos de referencia</Label>
                        <TaskAttachments
                          entityType="task"
                          entityId={selectedTarea.id}
                          attachments={adjuntosGestionLider}
                          onAttachmentsChange={setAdjuntosGestionLider}
                          canUpload={true}
                          compact={true}
                        />
                      </div>
                    </div>
                  )}
                  
                  {!accionLider && (
                    <div className="flex items-center justify-center h-32 text-slate-400 text-sm">
                      Seleccione una opción para continuar
                    </div>
                  )}
                </div>
              </div>

              {/* Botón de acción alineado a la derecha */}
              <div className='flex justify-end mt-6 pt-4 border-t border-slate-100'>
                {accionLider === 'auto_resolver' && (
                  <Button
                    className="bg-[#1E3649] hover:bg-[#2A4A5F] text-white px-6"
                    onClick={handleAutoResolverLider}
                    disabled={!comentarioLider.trim() || submittingLider}
                    data-testid="confirmar-auto-resolver"
                  >
                    {submittingLider ? 'Enviando...' : 'Resolver y Reenviar a Socio'}
                  </Button>
                )}
                {accionLider === 'derivar' && (
                  <Button
                    className="bg-[#1E3649] hover:bg-[#2A4A5F] text-white px-6"
                    onClick={handleDerivarFuncionario}
                    disabled={!comentarioLider.trim() || !funcionarioDerivacion.id || !plazoDerivacion || submittingLider}
                    data-testid="confirmar-derivar"
                  >
                    {submittingLider ? 'Derivando...' : 'Derivar a Funcionario'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Responder Referencia */}
      <Dialog open={showResponderReferencia} onOpenChange={(open) => {
        setShowResponderReferencia(open);
        if (!open) {
          setReferenciaSeleccionada(null);
          setRespuestaReferencia('');
          setAdjuntosRespuesta([]);
        }
      }}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700 text-lg font-semibold">
              <MessageSquare className="w-5 h-5" />
              Responder Referencia
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Responde al comentario donde fuiste mencionado. Tu respuesta quedará registrada en la bitácora.
            </DialogDescription>
          </DialogHeader>
          
          {referenciaSeleccionada && (
            <div className="mt-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Columna Izquierda: Contexto */}
                <div className="space-y-4">
                  {/* Quién te mencionó */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-600 mb-1">Mencionado por:</p>
                    <p className="font-bold text-blue-800 text-lg">{referenciaSeleccionada.mencionado_por}</p>
                    <p className="text-xs text-blue-500 mt-1">
                      {new Date(referenciaSeleccionada.created_at).toLocaleString('es-CL')}
                    </p>
                  </div>
                  
                  {/* Comentario de origen */}
                  <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
                    <p className="text-sm font-semibold text-amber-700 mb-2">
                      Comentario donde fuiste mencionado:
                    </p>
                    <p className="text-slate-800 italic whitespace-pre-wrap">
                      "{referenciaSeleccionada.mensaje_original}"
                    </p>
                  </div>
                  
                  {/* Información de la tarea */}
                  {referenciaSeleccionada.task_info && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                      <p className="text-sm font-semibold text-slate-600">Información de la Tarea:</p>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-green-50 rounded p-2">
                          <p className="text-xs text-green-600">Cliente</p>
                          <p className="font-semibold text-green-800">{referenciaSeleccionada.task_info.client_name}</p>
                        </div>
                        <div className="bg-purple-50 rounded p-2">
                          <p className="text-xs text-purple-600">PSP</p>
                          <p className="font-semibold text-purple-800">N° {referenciaSeleccionada.task_info.psp_folio || 'S/F'}</p>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-xs text-slate-500">Tarea:</p>
                        <p className="font-medium text-slate-800">{referenciaSeleccionada.task_info.task_title}</p>
                        <p className="text-sm text-slate-600 mt-1">{referenciaSeleccionada.task_info.task_description}</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Columna Derecha: Respuesta */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Tu Respuesta <span className="text-red-500">*</span></Label>
                    <Textarea
                      value={respuestaReferencia}
                      onChange={(e) => setRespuestaReferencia(e.target.value)}
                      placeholder="Escribe tu respuesta al comentario..."
                      rows={6}
                      className="resize-none"
                      data-testid="respuesta-referencia-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Esta respuesta quedará registrada en la bitácora de la tarea
                    </p>
                  </div>
                  
                  {/* Adjuntar archivos */}
                  <div className="border border-green-200 rounded-lg p-4 bg-green-50/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Paperclip className="w-4 h-4 text-green-600" />
                      <Label className="text-sm font-medium text-green-800">Adjuntar Evidencias (opcional)</Label>
                    </div>
                    <p className="text-xs text-green-600 mb-3">
                      Adjunte documentos, imágenes o PDFs que respalden su respuesta
                    </p>
                    <TaskAttachments
                      entityType="referencia"
                      entityId={referenciaSeleccionada.id}
                      attachments={adjuntosRespuesta}
                      onAttachmentsChange={setAdjuntosRespuesta}
                      canUpload={true}
                    />
                  </div>
                </div>
              </div>
              
              {/* Botones */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
                <Button 
                  variant="outline" 
                  onClick={() => setShowResponderReferencia(false)}
                  className="px-4"
                >
                  Cancelar
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6"
                  onClick={handleResponderReferencia}
                  disabled={!respuestaReferencia.trim() || respondiendoRef}
                  data-testid="confirmar-respuesta-ref"
                >
                  {respondiendoRef ? 'Enviando...' : 'Enviar Respuesta'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Ver Minutas de PSP (Referencia) */}
      <Dialog open={showMinutasRef} onOpenChange={setShowMinutasRef}>
        <DialogContent className="max-w-4xl w-[90vw] lg:w-[70vw] bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700 text-lg font-semibold">
              <FileText className="w-5 h-5" />
              Minutas de la PSP
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {tareaRefSeleccionada?.client_name && (
                <span>Cliente: <strong>{tareaRefSeleccionada.client_name}</strong> | PSP: <strong>N° {tareaRefSeleccionada.psp_folio || 'S/F'}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            {loadingMinutas ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full" />
                <span className="ml-2 text-slate-500">Cargando minutas...</span>
              </div>
            ) : minutasPsp.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">No hay minutas asociadas a esta PSP</p>
              </div>
            ) : (
              <div className="space-y-3">
                {minutasPsp.map((minuta) => (
                  <div 
                    key={minuta.id}
                    className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{minuta.title || 'Minuta sin título'}</p>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                          <span>Cliente: {minuta.client_name}</span>
                          {minuta.created_at && (
                            <span>Fecha: {new Date(minuta.created_at).toLocaleDateString('es-CL')}</span>
                          )}
                          {minuta.tareas_count > 0 && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">
                              {minuta.tareas_count} tarea(s)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                      onClick={() => handleViewPDF(minuta.id)}
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Ver PDF
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex justify-end mt-6 pt-4 border-t border-slate-100">
              <Button variant="outline" onClick={() => setShowMinutasRef(false)}>Cerrar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Ver Bitácora (Referencia) */}
      <Dialog open={showBitacoraRef} onOpenChange={setShowBitacoraRef}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1E3649] text-lg font-semibold">
              <MessageSquare className="w-5 h-5" />
              Bitácora de la Tarea
            </DialogTitle>
          </DialogHeader>
          {tareaRefSeleccionada && (
            <div className="mt-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
                <p className="font-medium text-slate-800">{tareaRefSeleccionada.title}</p>
                <p className="text-sm text-slate-600 mt-1">{tareaRefSeleccionada.description}</p>
              </div>
              
              <div className="space-y-3">
                {(!tareaRefSeleccionada.gestiones || tareaRefSeleccionada.gestiones.length === 0) ? (
                  <p className="text-slate-500 text-center py-8">No hay gestiones registradas</p>
                ) : (
                  tareaRefSeleccionada.gestiones.map((gestion, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border ${
                      gestion.tipo === 'completada' ? 'bg-blue-50 border-blue-200' :
                      gestion.tipo === 'aprobacion' ? 'bg-green-50 border-green-200' :
                      gestion.tipo === 'rechazo' ? 'bg-red-50 border-red-200' :
                      gestion.tipo === 'instruccion_inicial' ? 'bg-blue-50 border-blue-300' :
                      gestion.tipo === 'respuesta_mencion' ? 'bg-purple-50 border-purple-200' :
                      'bg-white border-slate-200'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-slate-800">{gestion.usuario}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            gestion.tipo === 'instruccion_inicial' ? 'bg-blue-200 text-blue-800' :
                            gestion.tipo === 'respuesta_mencion' ? 'bg-purple-200 text-purple-800' :
                            'bg-slate-200 text-slate-800'
                          }`}>
                            {gestion.tipo === 'instruccion_inicial' ? 'Instrucción Inicial' : 
                             gestion.tipo === 'respuesta_mencion' ? 'Respuesta a Mención' : 
                             gestion.tipo || 'Gestión'}
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">
                          {new Date(gestion.fecha).toLocaleString('es-CL')}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700">{gestion.descripcion}</p>
                      {/* Mostrar adjuntos de la gestión */}
                      {gestion.attachments?.length > 0 && (
                        <GestionAttachments attachmentIds={gestion.attachments} />
                      )}
                    </div>
                  ))
                )}
              </div>
              
              <div className="flex justify-end mt-6 pt-4 border-t border-slate-100">
                <Button variant="outline" onClick={() => setShowBitacoraRef(false)}>Cerrar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Ver Credenciales (Referencia) */}
      <Dialog open={showCredencialesRef} onOpenChange={setShowCredencialesRef}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600 text-lg font-semibold">
              <Key className="w-5 h-5" />
              Credenciales del Cliente
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Credenciales de acceso para el cliente {tareaRefSeleccionada?.client_name}
            </DialogDescription>
          </DialogHeader>
          {tareaRefSeleccionada?.client_id && (
            <div className="mt-4">
              <ClientCredentials clientId={tareaRefSeleccionada.client_id} canEdit={true} />
              <div className="flex justify-end mt-6 pt-4 border-t border-slate-100">
                <Button variant="outline" onClick={() => setShowCredencialesRef(false)}>Cerrar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Ver Adjuntos (Referencia) */}
      <Dialog open={showAdjuntosRef} onOpenChange={setShowAdjuntosRef}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600 text-lg font-semibold">
              <Paperclip className="w-5 h-5" />
              Documentos Adjuntos
            </DialogTitle>
          </DialogHeader>
          {tareaRefSeleccionada && (
            <div className="mt-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
                <p className="font-medium text-slate-800">{tareaRefSeleccionada.title}</p>
              </div>
              
              <TaskAttachments
                entityType="task"
                entityId={tareaRefSeleccionada.id}
                attachments={tareaRefSeleccionada.attachments || []}
                canUpload={false}
                showBitacoraAttachments={true}
              />
              
              <div className="flex justify-end mt-6 pt-4 border-t border-slate-100">
                <Button variant="outline" onClick={() => setShowAdjuntosRef(false)}>Cerrar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Ver Credenciales de Tarea */}
      <Dialog open={showCredencialesTarea} onOpenChange={setShowCredencialesTarea}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700 text-lg font-semibold">
              <Key className="w-5 h-5" />
              Credenciales del Cliente
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Credenciales de acceso para {selectedTarea?.client_name}
            </DialogDescription>
          </DialogHeader>
          {selectedTarea?.client_id && (
            <div className="mt-4">
              <ClientCredentials clientId={selectedTarea.client_id} canEdit={true} />
              <div className="flex justify-end mt-6 pt-4 border-t border-slate-100">
                <Button variant="outline" onClick={() => setShowCredencialesTarea(false)}>Cerrar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== MODALES TAREAS PERIÓDICAS ==================== */}
      
      {/* Modal Crear Tarea Periódica */}
      <Dialog open={showCrearPeriodica} onOpenChange={setShowCrearPeriodica}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-700 text-lg font-semibold">
              <RefreshCw className="w-5 h-5" />
              Nueva Tarea Periódica
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Crea una tarea que se repetirá automáticamente según la frecuencia seleccionada
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Columna izquierda: Info básica */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Título de la Tarea *</Label>
                  <Input
                    value={periodicaForm.titulo}
                    onChange={(e) => setPeriodicaForm(prev => ({ ...prev, titulo: e.target.value }))}
                    placeholder="Ej: Revisión mensual de F29"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-slate-700">Descripción</Label>
                  <Textarea
                    value={periodicaForm.descripcion}
                    onChange={(e) => setPeriodicaForm(prev => ({ ...prev, descripcion: e.target.value }))}
                    placeholder="Descripción detallada de la tarea..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
                
                {/* Checkbox Sin Cliente */}
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <input
                    type="checkbox"
                    id="sin-cliente"
                    checked={periodicaForm.sin_cliente}
                    onChange={(e) => {
                      setPeriodicaForm(prev => ({ 
                        ...prev, 
                        sin_cliente: e.target.checked,
                        client_id: e.target.checked ? '' : prev.client_id,
                        psp_id: e.target.checked ? '' : prev.psp_id
                      }));
                      if (e.target.checked) setPspsCliente([]);
                    }}
                    className="w-4 h-4 text-indigo-600 rounded"
                  />
                  <Label htmlFor="sin-cliente" className="text-sm text-slate-700 cursor-pointer">
                    Sin cliente asociado
                  </Label>
                </div>
                
                {/* Cliente y PSP - Solo visible si no marcó "sin cliente" */}
                {!periodicaForm.sin_cliente && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Cliente <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={periodicaForm.client_id || "_none"}
                        onValueChange={(val) => handleClienteChange(val === "_none" ? "" : val)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Seleccionar cliente" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">Seleccionar cliente...</SelectItem>
                          {clientes.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">PSP del Cliente</Label>
                      <Select
                        value={periodicaForm.psp_id || "_none"}
                        onValueChange={(val) => setPeriodicaForm(prev => ({ ...prev, psp_id: val === "_none" ? "" : val }))}
                        disabled={!periodicaForm.client_id}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder={periodicaForm.client_id ? "Seleccionar PSP" : "Seleccione cliente primero"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">Sin PSP</SelectItem>
                          {pspsCliente.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.folio}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {periodicaForm.client_id && pspsCliente.length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">Este cliente no tiene PSPs activas</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Columna derecha: Programación */}
              <div className="space-y-4 bg-indigo-50/50 p-4 rounded-lg border border-indigo-100">
                <h3 className="font-semibold text-indigo-800 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Programación de Recurrencia
                </h3>
                
                <div>
                  <Label className="text-sm font-medium text-slate-700">Fecha de Inicio *</Label>
                  <Input
                    type="date"
                    value={periodicaForm.fecha_inicio}
                    onChange={(e) => setPeriodicaForm(prev => ({ ...prev, fecha_inicio: e.target.value + 'T09:00:00Z' }))}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-slate-700">Frecuencia</Label>
                  <Select
                    value={periodicaForm.frecuencia}
                    onValueChange={(val) => setPeriodicaForm(prev => ({ ...prev, frecuencia: val }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="diaria">Diaria</SelectItem>
                      <SelectItem value="semanal">Semanal</SelectItem>
                      <SelectItem value="mensual">Mensual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {periodicaForm.frecuencia === 'semanal' && (
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Día de la Semana</Label>
                    <Select
                      value={periodicaForm.dia_semana?.toString() || '1'}
                      onValueChange={(val) => setPeriodicaForm(prev => ({ ...prev, dia_semana: parseInt(val) }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Lunes</SelectItem>
                        <SelectItem value="2">Martes</SelectItem>
                        <SelectItem value="3">Miércoles</SelectItem>
                        <SelectItem value="4">Jueves</SelectItem>
                        <SelectItem value="5">Viernes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {periodicaForm.frecuencia === 'mensual' && (
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Día del Mes</Label>
                    <Select
                      value={periodicaForm.dia_mes?.toString() || '1'}
                      onValueChange={(val) => setPeriodicaForm(prev => ({ ...prev, dia_mes: parseInt(val) }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(28)].map((_, i) => (
                          <SelectItem key={i + 1} value={(i + 1).toString()}>Día {i + 1}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div>
                  <Label className="text-sm font-medium text-slate-700">Número de Repeticiones (máx. 50)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    value={periodicaForm.repeticiones_total}
                    onChange={(e) => setPeriodicaForm(prev => ({ 
                      ...prev, 
                      repeticiones_total: Math.min(50, Math.max(1, parseInt(e.target.value) || 1)) 
                    }))}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    La tarea se repetirá {periodicaForm.repeticiones_total} veces
                  </p>
                </div>
              </div>
            </div>
            
            {/* Adjuntos */}
            <div className="border-t border-slate-200 pt-4">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Archivos Adjuntos (Opcional)</Label>
              <TaskAttachments
                entityType="tarea_periodica"
                entityId="new"
                attachments={adjuntosPeriodica}
                onAttachmentsChange={setAdjuntosPeriodica}
                canUpload={true}
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <Button variant="outline" onClick={() => setShowCrearPeriodica(false)}>Cancelar</Button>
              <Button 
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={handleCrearPeriodica}
                disabled={!periodicaForm.titulo.trim() || !periodicaForm.fecha_inicio || (!periodicaForm.sin_cliente && !periodicaForm.client_id)}
                data-testid="crear-periodica-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear Tarea Periódica
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Cumplir Tarea Periódica */}
      <Dialog open={showCumplirPeriodica} onOpenChange={(open) => {
        setShowCumplirPeriodica(open);
        if (!open) {
          setFeedbackPeriodica('');
          setAdjuntosPeriodica([]);
        }
      }}>
        <DialogContent className="max-w-4xl w-[90vw] lg:w-[70vw] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700 text-lg font-semibold">
              <CheckCircle className="w-5 h-5" />
              Completar Ciclo de Tarea Periódica
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Ciclo {(periodicaSeleccionada?.repeticiones_completadas || 0) + 1} de {periodicaSeleccionada?.repeticiones_total}
            </DialogDescription>
          </DialogHeader>
          
          {periodicaSeleccionada && (
            <div className="mt-4 space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800">{periodicaSeleccionada.titulo}</h3>
                {periodicaSeleccionada.descripcion && (
                  <p className="text-sm text-slate-600 mt-1">{periodicaSeleccionada.descripcion}</p>
                )}
                <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                  <span>Frecuencia: {getFrecuenciaLabel(periodicaSeleccionada.frecuencia)}</span>
                  {periodicaSeleccionada.client_name && (
                    <span>Cliente: {periodicaSeleccionada.client_name}</span>
                  )}
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium text-slate-700">
                  Feedback de Ejecución * <span className="text-red-500">(Obligatorio)</span>
                </Label>
                <Textarea
                  value={feedbackPeriodica}
                  onChange={(e) => setFeedbackPeriodica(e.target.value)}
                  placeholder="Describa las acciones realizadas para completar esta tarea..."
                  className="mt-1"
                  rows={4}
                />
                <p className="text-xs text-slate-500 mt-1">Mínimo 5 caracteres</p>
              </div>
              
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Adjuntos (Opcional)</Label>
                <TaskAttachments
                  entityType="cumplimiento_periodica"
                  entityId={periodicaSeleccionada.id}
                  attachments={adjuntosPeriodica}
                  onAttachmentsChange={setAdjuntosPeriodica}
                  canUpload={true}
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <Button variant="outline" onClick={() => setShowCumplirPeriodica(false)}>Cancelar</Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleCumplirPeriodica}
                  disabled={!feedbackPeriodica.trim() || feedbackPeriodica.trim().length < 5}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmar Cumplimiento
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Historial Tarea Periódica */}
      <Dialog open={showHistorialPeriodica} onOpenChange={setShowHistorialPeriodica}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-700 text-lg font-semibold">
              <History className="w-5 h-5" />
              Historial de Cumplimientos
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {periodicaSeleccionada?.titulo}
            </DialogDescription>
          </DialogHeader>
          
          {periodicaSeleccionada && (
            <div className="mt-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Progreso total</p>
                    <p className="text-2xl font-bold text-slate-800">
                      {periodicaSeleccionada.repeticiones_completadas} / {periodicaSeleccionada.repeticiones_total}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-600">Frecuencia</p>
                    <p className="font-semibold text-slate-800">{getFrecuenciaLabel(periodicaSeleccionada.frecuencia)}</p>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div 
                      className="bg-indigo-500 h-3 rounded-full transition-all"
                      style={{ width: `${(periodicaSeleccionada.repeticiones_completadas / periodicaSeleccionada.repeticiones_total) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
              
              {periodicaSeleccionada.historial?.length > 0 ? (
                <div className="space-y-3">
                  {periodicaSeleccionada.historial.slice().reverse().map((h, idx) => (
                    <div key={h.id || idx} className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                          Ciclo #{h.ciclo_numero}
                        </span>
                        <span className="text-xs text-slate-500">
                          {new Date(h.fecha).toLocaleString('es-CL')}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700">{h.feedback}</p>
                      <p className="text-xs text-slate-500 mt-2">Por: {h.usuario_name}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No hay cumplimientos registrados</p>
              )}
              
              <div className="flex justify-end mt-6 pt-4 border-t border-slate-100">
                <Button variant="outline" onClick={() => setShowHistorialPeriodica(false)}>Cerrar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MisTareas;
